import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configurar interceptor para añadir token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('lexduo_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de respuesta: detectar token expirado y forzar logout
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      if (!isLoginRequest) {
        localStorage.removeItem('lexduo_token');
        localStorage.removeItem('lexduo_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => axios.post(`${API}/auth/login`, data),
  register: (data) => axios.post(`${API}/auth/register`, data),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => axios.get(`${API}/dashboard/stats`),
  getExpedientesRecientes: () => axios.get(`${API}/dashboard/expedientes-recientes`),
  getEventosProximos: () => axios.get(`${API}/dashboard/eventos-proximos`),
};

// Expedientes
export const expedientesAPI = {
  getAll: (params) => axios.get(`${API}/expedientes`, { params }),
  getById: (id) => axios.get(`${API}/expedientes/${id}`),
  getNavegacion: (id) => axios.get(`${API}/expedientes/navegacion/${id}`),
  busquedaRapida: (q, limit = 10) => axios.get(`${API}/expedientes/busqueda-rapida`, { params: { q, limit } }),
  auditoriaIAPanel: (params = {}) => axios.get(`${API}/expedientes/auditoria-ia/panel`, { params }),
  auditoriaIAMarcar: (data) => axios.post(`${API}/expedientes/auditoria-ia/marcar`, data),
  auditoriaIABorrar: (actuacion_ids) => axios.post(`${API}/expedientes/auditoria-ia/borrar`, { actuacion_ids }),
  auditoriaIAContarAntiguas: () => axios.get(`${API}/expedientes/auditoria-ia/limpiar-antiguas`),
  auditoriaIALimpiarAntiguas: () => axios.post(`${API}/expedientes/auditoria-ia/limpiar-antiguas`),
  auditoriaIAContarDuplicadas: () => axios.get(`${API}/expedientes/auditoria-ia/limpiar-duplicadas`),
  auditoriaIABorradores: (params) => axios.get(`${API}/expedientes/auditoria-ia/borradores`, { params }),
  auditoriaIABorrarBorradores: (ids) => axios.post(`${API}/expedientes/auditoria-ia/borradores/borrar`, { ids }),
  auditoriaIALimpiarDuplicadas: () => axios.post(`${API}/expedientes/auditoria-ia/limpiar-duplicadas`),
  auditoriaIAExportar: (params = {}) => axios.get(`${API}/expedientes/auditoria-ia/exportar`, { params, responseType: 'blob' }),
  encargosIAListar: (params = {}) => axios.get(`${API}/expedientes/auditoria-ia/encargos`, { params }),
  encargosIADecidir: (data) => axios.post(`${API}/expedientes/auditoria-ia/encargos/estado`, data),
  encargosIASincronizar: (completa = false) => axios.post(`${API}/expedientes/auditoria-ia/encargos/sincronizar`, null, { params: { completa } }),
  create: (data) => axios.post(`${API}/expedientes`, data),
  update: (id, data) => axios.put(`${API}/expedientes/${id}`, data),
  delete: (id) => axios.delete(`${API}/expedientes/${id}`),
  
  // Actuaciones
  getActuaciones: (expedienteId) => axios.get(`${API}/expedientes/${expedienteId}/actuaciones`),
  getFacturas: (expedienteId) => axios.get(`${API}/expedientes/${expedienteId}/facturas`),
  createActuacion: (expedienteId, data) => axios.post(`${API}/expedientes/${expedienteId}/actuaciones`, data),
  updateActuacion: (actuacionId, data) => axios.put(`${API}/expedientes/actuaciones/${actuacionId}`, data),
  deleteActuacion: (actuacionId) => axios.delete(`${API}/expedientes/actuaciones/${actuacionId}`),
  getAllActuaciones: (params) => axios.get(`${API}/expedientes/all/actuaciones`, { params }),
  aiSearchActuaciones: (data) => axios.post(`${API}/expedientes/all/ai-search-actuaciones`, data),
  aiChatActuaciones: (data) => axios.post(`${API}/expedientes/all/ai-chat-actuaciones`, data),
  aiReportActuaciones: (data, format) => axios.post(`${API}/expedientes/all/ai-report-actuaciones`, data, { responseType: 'blob' }),
  getByJuzgado: (juzgadoId) => axios.get(`${API}/expedientes/by-juzgado/${juzgadoId}`),
  
  // Supervisión
  getSupervisionSinActividad: (meses = 1) => axios.get(`${API}/expedientes/supervision/sin-actividad`, { params: { meses } }),
  getSupervisionResumen: () => axios.get(`${API}/expedientes/supervision/resumen`),
};

// Procedimientos judiciales (v129)
export const procedimientosAPI = {
  config: () => axios.get(`${API}/expedientes/procedimientos/config`),
  list: (expedienteId) => axios.get(`${API}/expedientes/${expedienteId}/procedimientos`),
  create: (expedienteId, data) => axios.post(`${API}/expedientes/${expedienteId}/procedimientos`, data),
  update: (pid, data) => axios.put(`${API}/expedientes/procedimientos/${pid}`, data),
  remove: (pid) => axios.delete(`${API}/expedientes/procedimientos/${pid}`),
  importarHistorico: (expedienteId) => axios.post(`${API}/expedientes/${expedienteId}/procedimientos/importar-historico`),
  resumen: (ids) => axios.post(`${API}/expedientes/procedimientos/resumen`, { ids }),
};

// Agente IA (proxy hacia el servicio externo del agente)
export const agenteAPI = {
  estado: () => axios.get(`${API}/agente/estado`),
  encargar: (data) => axios.post(`${API}/agente/encargar`, data),
  estadoEncargo: (taskId) => axios.get(`${API}/agente/encargos/${taskId}`),
};

// Escritos IA (generación nativa con Claude)
export const escritosIaAPI = {
  modelos: () => axios.get(`${API}/escritos-ia/modelos`),
  generar: (data) => axios.post(`${API}/escritos-ia/generar`, data),
  word: (data) => axios.post(`${API}/escritos-ia/word`, data, { responseType: 'blob' }),
  verAdjunto: (id) => axios.get(`${API}/adjuntos/download/${id}`, { responseType: 'blob' }),
};

// Contactos
export const contactosAPI = {
  getAll: (params) => axios.get(`${API}/contactos`, { params }),
  getById: (id) => axios.get(`${API}/contactos/${id}`),
  create: (data) => axios.post(`${API}/contactos`, data),
  update: (id, data) => axios.put(`${API}/contactos/${id}`, data),
  delete: (id) => axios.delete(`${API}/contactos/${id}`),
  getExpedientes: (id) => axios.get(`${API}/contactos/${id}/expedientes`),
  getFacturas: (id) => axios.get(`${API}/contactos/${id}/facturas`),
  getTareas: (id) => axios.get(`${API}/contactos/${id}/tareas`),
  getDuplicados: () => axios.get(`${API}/contactos/duplicados`),
  getDuplicadosJuzgados: () => axios.get(`${API}/contactos/duplicados-juzgados`),
  descartarDuplicado: (data) => axios.post(`${API}/contactos/duplicados/descartar`, data),
  fusionar: (data) => axios.post(`${API}/contactos/fusionar`, data),
};

// Facturas
export const facturasAPI = {
  getAll: (params) => axios.get(`${API}/facturas`, { params }),
  getById: (id) => axios.get(`${API}/facturas/${id}`),
  create: (data) => axios.post(`${API}/facturas`, data),
  update: (id, data) => axios.put(`${API}/facturas/${id}`, data),
  delete: (id) => axios.delete(`${API}/facturas/${id}`),
  marcarPagada: (id) => axios.post(`${API}/facturas/${id}/marcar-pagada`),
  getStats: (params) => axios.get(`${API}/facturas/stats/resumen`, { params }),
  getEjercicios: () => axios.get(`${API}/facturas/ejercicios`),
  previewNumero: (params) => axios.get(`${API}/facturas/preview-numero`, { params }),
  // Nuevas funcionalidades
  convertirAFactura: (id) => axios.post(`${API}/facturas/${id}/convertir-a-factura`),
  // Factura rectificativa con serie propia (art. 6.1.a RD 1619/2012)
  emitirRectificativa: (id, data) => axios.post(`${API}/facturas/${id}/rectificativa`, data),
  registrarPago: (id, data) => axios.post(`${API}/facturas/${id}/pagos`, data),
  eliminarPago: (facturaId, pagoId) => axios.delete(`${API}/facturas/${facturaId}/pagos/${pagoId}`),
  getPdfUrl: (id) => `${API}/facturas/${id}/pdf`,
  descargarPdf: (id) => axios.get(`${API}/facturas/${id}/pdf`, { responseType: 'blob' }),
  getEmisor: (id) => axios.get(`${API}/facturas/${id}/emisor`),
  previewEmisorPdf: (data) =>
    axios.post(`${API}/facturas/preview-emisor-pdf`, data, { responseType: 'blob' }),
  getEmailInfo: (id, idioma = 'es') =>
    axios.get(`${API}/facturas/${id}/email-info`, { params: { idioma } }),
  enviarEmail: (id, data) => axios.post(`${API}/facturas/${id}/enviar-email`, data),
  getSinFacturador: () => axios.get(`${API}/facturas/sin-facturador`),
  asignarFacturador: (id, data) => axios.patch(`${API}/facturas/${id}/facturador`, data),
  // VERIFACTU / SIF — emisión y anulación fiscal + envío email
  emitirFiscal: (id) => axios.post(`${API}/facturas/${id}/emitir-fiscal`),
  anularFiscal: (id, motivo) => axios.post(`${API}/facturas/${id}/anular-fiscal`, { motivo }),
  enviarEmailCliente: (id, destinatario) =>
    axios.post(`${API}/facturas/${id}/enviar-email-cliente`, { destinatario: destinatario || null }),
};

// VERIFACTU / SIF
export const verifactuAPI = {
  getState: (facturaId) => axios.get(`${API}/verifactu/state/${facturaId}`),
  verifyChain: (nif) => axios.get(`${API}/verifactu/verify-chain/${encodeURIComponent(nif)}`),
  getAudit: (facturaId) => axios.get(`${API}/verifactu/audit/${facturaId}`),
  qrPreview: (facturaId) => axios.get(`${API}/verifactu/qr-preview/${facturaId}`),
};

// Eventos
export const eventosAPI = {
  getAll: (params) => axios.get(`${API}/eventos`, { params }),
  getById: (id) => axios.get(`${API}/eventos/${id}`),
  create: (data) => axios.post(`${API}/eventos`, data),
  update: (id, data) => axios.put(`${API}/eventos/${id}`, data),
  delete: (id) => axios.delete(`${API}/eventos/${id}`),
  getProximos: (dias = 7) => axios.get(`${API}/eventos/proximos`, { params: { dias } }),
  getMes: (year, month) => axios.get(`${API}/eventos/mes/${year}/${month}`),
  // Nuevos endpoints
  getByExpediente: (expedienteId) => axios.get(`${API}/eventos/expediente/${expedienteId}`),
  createForExpediente: (expedienteId, data) => axios.post(`${API}/eventos/expediente/${expedienteId}`, data),
  getCalendarios: () => axios.get(`${API}/eventos/calendarios`),
  getTipos: () => axios.get(`${API}/eventos/tipos`),
  getAlertasOpciones: () => axios.get(`${API}/eventos/alertas-opciones`),
};

// Informes
export const informesAPI = {
  getTipos: () => axios.get(`${API}/informes/tipos`),
  getTiposPorCategoria: (tipo) => axios.get(`${API}/informes/tipos/${tipo}`),
  generar: (informeId, params) => axios.get(`${API}/informes/generar/${informeId}`, { params }),
  getFiltros: () => axios.get(`${API}/informes/filtros`),
};

// Comunicaciones
export const comunicacionesAPI = {
  getClientes: (params) => axios.get(`${API}/comunicaciones/clientes`, { params }),
  getTodosContactos: (params) => axios.get(`${API}/comunicaciones/contactos-todos`, { params }),
  enviar: (data) => axios.post(`${API}/comunicaciones/enviar`, data),
  enviarConAdjuntos: (formData) => axios.post(`${API}/comunicaciones/enviar-adjuntos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getHistorial: (params) => axios.get(`${API}/comunicaciones/historial`, { params }),
  getDetalleComunicacion: (id) => axios.get(`${API}/comunicaciones/historial/${id}`),
  getEstadisticas: () => axios.get(`${API}/comunicaciones/estadisticas`),
  getPlantillas: () => axios.get(`${API}/comunicaciones/plantillas`),
};

// Usuarios (Backoffice)
export const usuariosAPI = {
  getAll: (params) => axios.get(`${API}/usuarios`, { params }),
  getById: (id) => axios.get(`${API}/usuarios/${id}`),
  create: (data) => axios.post(`${API}/usuarios`, data),
  update: (id, data) => axios.put(`${API}/usuarios/${id}`, data),
  delete: (id) => axios.delete(`${API}/usuarios/${id}`),
  updatePermisos: (id, permisos) => axios.put(`${API}/usuarios/${id}/permisos`, permisos),
  updatePermisoModulo: (id, modulo, lectura, escritura) => 
    axios.put(`${API}/usuarios/${id}/permiso-modulo?modulo=${modulo}&lectura=${lectura}&escritura=${escritura}`),
  cambiarPassword: (id, password) => axios.post(`${API}/usuarios/${id}/cambiar-password?new_password=${password}`),
  getEstadisticas: (id) => axios.get(`${API}/usuarios/${id}/estadisticas`),
  getCargaTrabajo: () => axios.get(`${API}/usuarios/estadisticas/carga-trabajo`),
  getModulos: () => axios.get(`${API}/usuarios/modulos/disponibles`),
};

// Configuración
export const configuracionAPI = {
  // Items genéricos de configuración
  getCategorias: () => axios.get(`${API}/configuracion/categorias`),
  getItems: (categoria, params) => axios.get(`${API}/configuracion/items/${categoria}`, { params }),
  createItem: (data) => axios.post(`${API}/configuracion/items`, data),
  updateItem: (id, data) => axios.put(`${API}/configuracion/items/${id}`, data),
  deleteItem: (id) => axios.delete(`${API}/configuracion/items/${id}`),
  
  // Configuración de empresa
  getConfigEmpresa: (grupo) => axios.get(`${API}/configuracion/empresa`, { params: { grupo } }),
  updateConfigEmpresa: (data) => axios.put(`${API}/configuracion/empresa`, data),
  
  // Accesos rápidos
  getAccesosRapidos: () => axios.get(`${API}/configuracion/accesos-rapidos`),
  createAccesoRapido: (data) => axios.post(`${API}/configuracion/accesos-rapidos`, data),
  deleteAccesoRapido: (id) => axios.delete(`${API}/configuracion/accesos-rapidos/${id}`),
  
  // Calendarios
  getCalendarios: () => axios.get(`${API}/configuracion/calendarios`),
  createCalendario: (data) => axios.post(`${API}/configuracion/calendarios`, data),
  deleteCalendario: (id) => axios.delete(`${API}/configuracion/calendarios/${id}`),
  
  // Facturadores
  getFacturadores: (incluirInactivos = false) =>
    axios.get(`${API}/configuracion/facturadores`, {
      params: incluirInactivos ? { incluir_inactivos: true } : {},
    }),
  createFacturador: (data) => axios.post(`${API}/configuracion/facturadores`, data),
  updateFacturador: (id, data) => axios.put(`${API}/configuracion/facturadores/${id}`, data),
  deleteFacturador: (id) => axios.delete(`${API}/configuracion/facturadores/${id}`),
  toggleFacturadorActivo: (id, activo) =>
    axios.patch(`${API}/configuracion/facturadores/${id}/activo`, { activo }),
  uploadFacturadorLogo: (id, formData) =>
    axios.post(`${API}/configuracion/facturadores/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteFacturadorLogo: (id) => axios.delete(`${API}/configuracion/facturadores/${id}/logo`),
  toggleVerifactu: (id, activado, modo, auto_email_emision) =>
    axios.patch(`${API}/configuracion/facturadores/${id}/verifactu`, {
      activado, modo, auto_email_emision,
    }),
  
  // Grupos de usuarios
  getGruposUsuarios: () => axios.get(`${API}/configuracion/grupos-usuarios`),
  createGrupoUsuario: (data) => axios.post(`${API}/configuracion/grupos-usuarios`, data),
  deleteGrupoUsuario: (id) => axios.delete(`${API}/configuracion/grupos-usuarios/${id}`),
  
  // Campos personalizados
  getCamposPersonalizados: (entidad) => axios.get(`${API}/configuracion/campos-personalizados`, { params: { entidad } }),
  createCampoPersonalizado: (data) => axios.post(`${API}/configuracion/campos-personalizados`, data),
  deleteCampoPersonalizado: (id) => axios.delete(`${API}/configuracion/campos-personalizados/${id}`),
  
  // Historial de acciones
  getHistorialAcciones: (params) => axios.get(`${API}/configuracion/historial-acciones`, { params }),
  
  // Inicializar datos por defecto
  inicializarDatos: () => axios.post(`${API}/configuracion/inicializar-datos`),
};

// Chat AI
export const chatAPI = {
  send: (data) => axios.post(`${API}/chat`, data),
  sendWithFile: (message, history, file) => {
    const formData = new FormData();
    formData.append('message', message);
    formData.append('history', JSON.stringify(history));
    if (file) {
      formData.append('file', file);
    }
    return axios.post(`${API}/chat/with-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  health: () => axios.get(`${API}/chat/health`),
};

// AI Settings
export const aiSettingsAPI = {
  getFallback: () => axios.get(`${API}/ai-settings/fallback`),
  updateFallback: (data) => axios.put(`${API}/ai-settings/fallback`, data),
  testFallback: (data) => axios.post(`${API}/ai-settings/fallback/test`, data),

  get: () => axios.get(`${API}/ai-settings`),
  update: (data) => axios.put(`${API}/ai-settings`, data),
  test: (data) => axios.post(`${API}/ai-settings/test`, data),
  getPrompts: () => axios.get(`${API}/ai-settings/prompts`),

  // v141 - aviso por fallos de IA, proveedor por funcion y prueba de caida
  getAlerta: () => axios.get(`${API}/ai-settings/alerta`),
  updateAlerta: (data) => axios.put(`${API}/ai-settings/alerta`, data),
  getModulos: () => axios.get(`${API}/ai-settings/modulos`),
  updateModulo: (data) => axios.put(`${API}/ai-settings/modulos`, data),
  simularFallo: (data) => axios.post(`${API}/ai-settings/simular-fallo`, data),
  getModelosDisponibles: (provider) =>
    axios.get(`${API}/ai-settings/modelos-disponibles`, { params: provider ? { provider } : {} }),
};

// Participantes de expedientes
export const participantesAPI = {
  getRoles: () => axios.get(`${API}/participantes/roles`),
  getByExpediente: (expedienteId) => axios.get(`${API}/participantes/expediente/${expedienteId}`),
  getByContacto: (contactoId) => axios.get(`${API}/participantes/contacto/${contactoId}`),
  add: (data) => axios.post(`${API}/participantes`, data),
  create: (data) => axios.post(`${API}/participantes`, data), // Alias for add
  remove: (id) => axios.delete(`${API}/participantes/${id}`),
  updateRol: (id, nuevoRol) => axios.put(`${API}/participantes/${id}/rol?nuevo_rol=${nuevoRol}`),
};

// Adjuntos (archivos)
export const adjuntosAPI = {
  getTipos: () => axios.get(`${API}/adjuntos/tipos`),
  // Expedientes
  uploadExpediente: (expedienteId, formData) => axios.post(`${API}/adjuntos/expediente/${expedienteId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getByExpediente: (expedienteId) => axios.get(`${API}/adjuntos/expediente/${expedienteId}`),
  // Contactos
  uploadContacto: (contactoId, formData) => axios.post(`${API}/adjuntos/contacto/${contactoId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getByContacto: (contactoId) => axios.get(`${API}/adjuntos/contacto/${contactoId}`),
  // Actuaciones
  uploadActuacion: (actuacionId, formData) => axios.post(`${API}/adjuntos/actuacion/${actuacionId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getByActuacion: (actuacionId) => axios.get(`${API}/adjuntos/actuacion/${actuacionId}`),
  downloadZipActuacion: (actuacionId, ids = []) => axios.get(
    `${API}/adjuntos/actuacion/${actuacionId}/zip${ids.length ? `?${ids.map((i) => `ids=${i}`).join('&')}` : ''}`,
    { responseType: 'blob' }
  ),
  convertirMarkdown: (adjuntoId) => axios.post(`${API}/adjuntos/${adjuntoId}/convertir-md`),
  // v87: conversiones OCR en cola (PDF escaneados largos)
  ocrJobEstado: (jobId) => axios.get(`${API}/adjuntos/ocr-jobs/${jobId}`),
  ocrJobsActivos: () => axios.get(`${API}/adjuntos/ocr-jobs`),
  convertirPdf: (adjuntoId) => axios.post(`${API}/adjuntos/${adjuntoId}/convertir-pdf`),
  // Batch: contar adjuntos para varias actuaciones en una sola request
  getCountsActuaciones: (ids) => axios.get(`${API}/adjuntos/actuaciones/counts`, {
    params: { ids: (Array.isArray(ids) ? ids : [ids]).join(',') }
  }),
  // Tareas
  uploadTarea: (tareaId, formData) => axios.post(`${API}/adjuntos/tarea/${tareaId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getByTarea: (tareaId) => axios.get(`${API}/adjuntos/tarea/${tareaId}`),
  // Notas
  uploadNota: (notaId, formData) => axios.post(`${API}/adjuntos/nota/${notaId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getByNota: (notaId) => axios.get(`${API}/adjuntos/nota/${notaId}`),
  // Gastos
  uploadGasto: (gastoId, formData) => axios.post(`${API}/adjuntos/gasto/${gastoId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getByGasto: (gastoId) => axios.get(`${API}/adjuntos/gasto/${gastoId}`),
  // Común
  getInfo: (adjuntoId) => axios.get(`${API}/adjuntos/info/${adjuntoId}`),
  getUrl: (adjuntoId) => axios.get(`${API}/adjuntos/url/${adjuntoId}`),
  getPreviewUrl: (adjuntoId) => `${API}/adjuntos/preview/${adjuntoId}`,
  download: (adjuntoId) => axios.get(`${API}/adjuntos/download/${adjuntoId}`, { responseType: 'blob' }),
  downloadZip: (ids) => axios.post(`${API}/adjuntos/download-zip`, ids, { responseType: 'blob' }),
  delete: (adjuntoId) => axios.delete(`${API}/adjuntos/${adjuntoId}`),
  deleteBatch: (ids) => axios.delete(`${API}/adjuntos/batch`, { params: { ids } }),
  update: (adjuntoId, formData) => axios.put(`${API}/adjuntos/${adjuntoId}`, formData),
};

// Firma manuscrita de documentos por el cliente
export const firmasAPI = {
  preparar: (adjuntoId, clienteId) => axios.get(`${API}/firmas/preparar/${adjuntoId}`,
    clienteId ? { params: { cliente_id: clienteId } } : undefined),
  previsualizar: (adjuntoId, clienteId) => axios.get(`${API}/firmas/previsualizar/${adjuntoId}`,
    clienteId ? { params: { cliente_id: clienteId } } : undefined),
  solicitar: (data) => axios.post(`${API}/firmas/solicitar`, data),
  getByActuacion: (actuacionId) => axios.get(`${API}/firmas/actuacion/${actuacionId}`),
  getByExpediente: (expedienteId) => axios.get(`${API}/firmas/expediente/${expedienteId}`),
  pendientes: () => axios.get(`${API}/firmas/pendientes`),
  anadirFirmante: (firmaId, data) => axios.post(`${API}/firmas/${firmaId}/anadir-firmante`, data),
  renovar: (firmaId, data) => axios.post(`${API}/firmas/${firmaId}/renovar`, data || { enviar_email: true }),
  anular: (firmaId) => axios.post(`${API}/firmas/${firmaId}/anular`),
  cancelar: (firmaId, data) => axios.post(`${API}/firmas/${firmaId}/cancelar`, data),
  eliminar: (firmaId, params) => axios.delete(`${API}/firmas/${firmaId}`, { params }),
};

// Cliente público de firma: instancia propia SIN interceptores (un 401 aquí
// significa "NIF incorrecto", no debe redirigir al login del despacho)
const publicClient = axios.create();
export const firmasPublicAPI = {
  info: (token) => publicClient.get(`${API}/firmas/publico/${token}`),
  acceder: (token, nif) => publicClient.post(`${API}/firmas/publico/${token}/acceder`, { nif }),
  firmar: (token, data) => publicClient.post(`${API}/firmas/publico/${token}/firmar`, data),
  urlDescarga: (token, acceso) => `${API}/firmas/publico/${token}/descargar?acceso=${encodeURIComponent(acceso)}`,
};

// Tareas
export const tareasAPI = {
  getByExpediente: (expedienteId) => axios.get(`${API}/tareas/expediente/${expedienteId}`),
  create: (expedienteId, data) => axios.post(`${API}/tareas/expediente/${expedienteId}`, data),
  update: (tareaId, data) => axios.put(`${API}/tareas/${tareaId}`, data),
  delete: (tareaId) => axios.delete(`${API}/tareas/${tareaId}`),
};

// Notas
export const notasAPI = {
  getByExpediente: (expedienteId) => axios.get(`${API}/notas/expediente/${expedienteId}`),
  create: (expedienteId, data) => axios.post(`${API}/notas/expediente/${expedienteId}`, data),
  update: (notaId, data) => axios.put(`${API}/notas/${notaId}`, data),
  delete: (notaId) => axios.delete(`${API}/notas/${notaId}`),
};

// Admin - Configuración Email
export const adminEmailAPI = {
  getConfigs: () => axios.get(`${API}/admin/email/config`),
  getActive: () => axios.get(`${API}/admin/email/config/active`),
  create: (data) => axios.post(`${API}/admin/email/config`, data),
  update: (id, data) => axios.put(`${API}/admin/email/config/${id}`, data),
  delete: (id) => axios.delete(`${API}/admin/email/config/${id}`),
  test: (id) => axios.post(`${API}/admin/email/config/${id}/test`),
  sendTest: (id, email) => axios.post(`${API}/admin/email/config/${id}/send-test?email_to=${email}`),
};

// Admin - Categorías de Calendario
export const adminCalendarioAPI = {
  getCategorias: () => axios.get(`${API}/admin/calendario/categorias`),
  createCategoria: (data) => axios.post(`${API}/admin/calendario/categorias`, data),
  updateCategoria: (id, data) => axios.put(`${API}/admin/calendario/categorias/${id}`, data),
  deleteCategoria: (id) => axios.delete(`${API}/admin/calendario/categorias/${id}`),
};

// Configuración General (Logo, etc.)
export const configGeneralAPI = {
  // Logo
  getLogo: () => axios.get(`${API}/config/logo`),
  uploadLogo: (formData) => axios.post(`${API}/config/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLogo: () => axios.delete(`${API}/config/logo`),
  getLogoUrl: (filename) => `${API}/config/logo/${filename}`,
  
  // Logo settings (enable/disable upload)
  getLogoSettings: () => axios.get(`${API}/config/logo/settings`),
  setLogoUploadEnabled: (enabled) => axios.put(`${API}/config/logo/settings?enabled=${enabled}`),
  
  // Config general
  getConfig: (clave) => axios.get(`${API}/config/general/${clave}`),
  setConfig: (clave, valor) => axios.put(`${API}/config/general/${clave}`, { valor }),
  getAllConfig: () => axios.get(`${API}/config/all`),
};

// Cuentas de Email
export const cuentasEmailAPI = {
  getAll: (activasSolo = false) => axios.get(`${API}/cuentas-email`, { params: { activas_solo: activasSolo } }),
  getActivas: () => axios.get(`${API}/cuentas-email/activas`),
  getPrincipal: () => axios.get(`${API}/cuentas-email/principal`),
  getById: (id) => axios.get(`${API}/cuentas-email/${id}`),
  create: (data) => axios.post(`${API}/cuentas-email`, data),
  update: (id, data) => axios.put(`${API}/cuentas-email/${id}`, data),
  delete: (id) => axios.delete(`${API}/cuentas-email/${id}`),
  testSmtp: (id) => axios.post(`${API}/cuentas-email/${id}/test-smtp`),
  sendTestEmail: (id, emailTo) => axios.post(`${API}/cuentas-email/${id}/send-test?email_to=${emailTo}`),
  setPrincipal: (id) => axios.post(`${API}/cuentas-email/${id}/set-principal`),
};

// API de Controles de Gastos
export const gastosAPI = {
  // Tipos de gasto
  getTipos: () => axios.get(`${API}/gastos/tipos`),
  createTipo: (data) => axios.post(`${API}/gastos/tipos`, data),
  
  // Controles de gasto
  getAll: (params) => axios.get(`${API}/gastos`, { params }),
  getById: (id) => axios.get(`${API}/gastos/${id}`),
  create: (data) => axios.post(`${API}/gastos`, data),
  update: (id, data) => axios.put(`${API}/gastos/${id}`, data),
  delete: (id) => axios.delete(`${API}/gastos/${id}`),
  // Fecha de pago: es la que decide a qué trimestre se imputa la retención (modelo 111)
  marcarPago: (id, fechaPago) => axios.patch(`${API}/gastos/${id}/pago`, { fecha_pago: fechaPago || null }),
  marcarPagosLote: (ids, modo = 'fecha', fechaPago = null) => axios.post(`${API}/gastos/pagos-lote`, { ids, modo, fecha_pago: fechaPago }),
  // Clave de perceptor del 111 para una factura concreta (vacío = la de la ficha del contacto)
  setClave111: (id, tipo) => axios.patch(`${API}/gastos/${id}/clave-111`, { tipo_perceptor_111: tipo || null }),
  
  // Stats
  getStats: (params) => axios.get(`${API}/gastos/stats`, { params }),
  getCount: (params) => axios.get(`${API}/gastos/count`, { params }),
  // Duplicados de facturas de gasto
  getDuplicados: (params) => axios.get(`${API}/gastos/duplicados`, { params }),
  getGruposDuplicados: () => axios.get(`${API}/gastos/duplicados/grupos`),
  // Facturadores (filtro por usuario)
  getFacturadores: () => axios.get(`${API}/gastos/facturadores`),
  setFacturadorDefault: (facturador_id) => axios.post(`${API}/gastos/facturador-default`, { facturador_id }),

  // Entidad facturadora del despacho (Configuración → Facturadores) y divisas
  getFacturadoresEntidad: () => axios.get(`${API}/gastos/facturadores-entidad`),
  setEntidadDefault: (facturador_entidad_id) =>
    axios.post(`${API}/gastos/facturador-entidad-default`, { facturador_entidad_id }),
  getEntidadSugerida: (params) => axios.get(`${API}/gastos/entidad-sugerida`, { params }),
  getMonedas: () => axios.get(`${API}/gastos/monedas`),
  getTipoCambio: (params) => axios.get(`${API}/gastos/tipo-cambio`, { params }),
  
  // Exportación
  exportCSV: (params) => axios.get(`${API}/gastos/export/csv`, { params, responseType: 'blob' }),
  exportExcel: (params) => axios.get(`${API}/gastos/export/excel`, { params, responseType: 'blob' }),
};


// API de Morosidades
export const morosidadesAPI = {
  // CRUD principal
  getAll: (params) => axios.get(`${API}/morosidades`, { params }),
  getById: (id) => axios.get(`${API}/morosidades/${id}`),
  create: (data) => axios.post(`${API}/morosidades`, data),
  update: (id, data) => axios.put(`${API}/morosidades/${id}`, data),
  delete: (id) => axios.delete(`${API}/morosidades/${id}`),
  getStats: () => axios.get(`${API}/morosidades/stats`),
  getEstadisticasCompletas: () => axios.get(`${API}/morosidades/estadisticas/completas`),
  
  // Informe Word
  getInformeWord: (id) => axios.get(`${API}/morosidades/${id}/informe-word`, { responseType: 'blob' }),
  
  // Pagos
  getPagos: (morosidadId) => axios.get(`${API}/morosidades/${morosidadId}/pagos`),
  createPago: (morosidadId, data) => axios.post(`${API}/morosidades/${morosidadId}/pagos`, data),
  deletePago: (morosidadId, pagoId) => axios.delete(`${API}/morosidades/${morosidadId}/pagos/${pagoId}`),
  
  // Actuaciones
  getActuaciones: (morosidadId) => axios.get(`${API}/morosidades/${morosidadId}/actuaciones`),
  createActuacion: (morosidadId, data) => axios.post(`${API}/morosidades/${morosidadId}/actuaciones`, data),
  deleteActuacion: (morosidadId, actuacionId) => axios.delete(`${API}/morosidades/${morosidadId}/actuaciones/${actuacionId}`),
  
  // Adjuntos
  getAdjuntos: (morosidadId) => axios.get(`${API}/morosidades/${morosidadId}/adjuntos`),
  uploadAdjunto: (morosidadId, formData) => axios.post(`${API}/morosidades/${morosidadId}/adjuntos`, formData),
  downloadAdjunto: (morosidadId, adjuntoId) => axios.get(`${API}/morosidades/${morosidadId}/adjuntos/${adjuntoId}/download`, { responseType: 'blob' }),
  deleteAdjunto: (morosidadId, adjuntoId) => axios.delete(`${API}/morosidades/${morosidadId}/adjuntos/${adjuntoId}`),
};


// API de Tareas del Despacho
export const tareasDespachoAPI = {
  // Configuración
  getCategorias: () => axios.get(`${API}/tareas-despacho/categorias`),
  getPrioridades: () => axios.get(`${API}/tareas-despacho/prioridades`),
  getFasesMinutas: () => axios.get(`${API}/tareas-despacho/fases-minutas`),
  
  // Stats generales
  getStats: () => axios.get(`${API}/tareas-despacho/stats`),
  
  // === TAREA ABOGADOS ===
  abogados: {
    getAll: (params) => axios.get(`${API}/tareas-despacho/abogados`, { params }),
    getById: (id) => axios.get(`${API}/tareas-despacho/abogados/${id}`),
    create: (data) => axios.post(`${API}/tareas-despacho/abogados`, data),
    update: (id, data) => axios.put(`${API}/tareas-despacho/abogados/${id}`, data),
    delete: (id) => axios.delete(`${API}/tareas-despacho/abogados/${id}`),
    completar: (id, completada = true) => axios.patch(`${API}/tareas-despacho/abogados/${id}/completar?completada=${completada}`),
    // Archivos
    getArchivos: (tareaId) => axios.get(`${API}/tareas-despacho/abogados/${tareaId}/archivos`),
    uploadArchivo: (tareaId, file) => {
      const formData = new FormData();
      formData.append('file', file);
      return axios.post(`${API}/tareas-despacho/abogados/${tareaId}/archivos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    deleteArchivo: (archivoId) => axios.delete(`${API}/tareas-despacho/archivos/${archivoId}`),
    infoCompletar: (tareaId) => axios.get(`${API}/tareas-despacho/abogados/${tareaId}/info-completar`),
    crearActuacion: (tareaId) => axios.post(`${API}/tareas-despacho/abogados/${tareaId}/crear-actuacion`),
  },
  
  // === TAREA MINUTAS (Con workflow de 3 fases) ===
  minutas: {
    getAll: (params) => axios.get(`${API}/tareas-despacho/minutas`, { params }),
    getById: (id) => axios.get(`${API}/tareas-despacho/minutas/${id}`),
    create: (data) => axios.post(`${API}/tareas-despacho/minutas`, data),
    delete: (id) => axios.delete(`${API}/tareas-despacho/minutas/${id}`),
    // Actualizar por fases
    updateFaseA: (id, data) => axios.put(`${API}/tareas-despacho/minutas/${id}/fase-a`, data),
    updateFaseB: (id, data) => axios.put(`${API}/tareas-despacho/minutas/${id}/fase-b`, data),
    updateFaseC: (id, data) => axios.put(`${API}/tareas-despacho/minutas/${id}/fase-c`, data),
    // Nuevos endpoints
    getUltimoNumero: (id) => axios.get(`${API}/tareas-despacho/minutas/${id}/ultimo-numero`),
    getProcurador: (id) => axios.get(`${API}/tareas-despacho/minutas/${id}/procurador`),
    enviarEmail: (id, data) => axios.post(`${API}/tareas-despacho/minutas/${id}/enviar-email`, data || {}),
    crearActuacion: (id) => axios.post(`${API}/tareas-despacho/minutas/${id}/crear-actuacion`),
    // Archivos
    getArchivos: (id, fase) => axios.get(`${API}/tareas-despacho/minutas/${id}/archivos`, { params: { fase } }),
    uploadArchivo: (id, fase, file) => {
      const formData = new FormData();
      formData.append('file', file);
      return axios.post(`${API}/tareas-despacho/minutas/${id}/archivos?fase=${fase}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    deleteArchivo: (tareaId, archivoId) => axios.delete(`${API}/tareas-despacho/minutas/${tareaId}/archivos/${archivoId}`),
    // Estadísticas contabilidad
    getEstadisticasContabilidad: (anno) => axios.get(`${API}/tareas-despacho/minutas/estadisticas/contabilidad`, { params: { anno } }),
    getEvolucion: () => axios.get(`${API}/tareas-despacho/minutas/estadisticas/evolucion`),
    // Intereses
    getIntereses: (params) => axios.get(`${API}/tareas-despacho/minutas/intereses`, { params }),
  },
  // === PROPUESTAS DE INTERESES ===
  propuestasIntereses: {
    getAll: (params) => axios.get(`${API}/tareas-despacho/propuestas-intereses`, { params }),
    getById: (id) => axios.get(`${API}/tareas-despacho/propuestas-intereses/${id}`),
    create: (data) => axios.post(`${API}/tareas-despacho/propuestas-intereses`, data),
    update: (id, data) => axios.put(`${API}/tareas-despacho/propuestas-intereses/${id}`, data),
    delete: (id) => axios.delete(`${API}/tareas-despacho/propuestas-intereses/${id}`),
    crearActuacion: (id) => axios.post(`${API}/tareas-despacho/propuestas-intereses/${id}/crear-actuacion`),
    subirArchivo: (id, formData) => axios.post(`${API}/tareas-despacho/propuestas-intereses/${id}/subir-archivo`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
    eliminarArchivo: (tareaId, archivoId) => axios.delete(`${API}/tareas-despacho/propuestas-intereses/${tareaId}/archivo/${archivoId}`),
    getDownloadUrl: (tareaId, archivoId) => { const token = localStorage.getItem('lexduo_token'); return `${API}/tareas-despacho/propuestas-intereses/${tareaId}/archivo/${archivoId}/download?token=${token}`; },
  },
  
  // === TAREA COSTAS A PAGAR ===
  costas: {
    getAll: (params) => axios.get(`${API}/tareas-despacho/costas`, { params }),
    getById: (id) => axios.get(`${API}/tareas-despacho/costas/${id}`),
    create: (data) => axios.post(`${API}/tareas-despacho/costas`, data),
    update: (id, data) => axios.put(`${API}/tareas-despacho/costas/${id}`, data),
    delete: (id) => axios.delete(`${API}/tareas-despacho/costas/${id}`),
    completar: (id, completada = true) => axios.patch(`${API}/tareas-despacho/costas/${id}/completar?completada=${completada}`),
  },
  
  // === TAREA EJECUCIONES ===
  ejecuciones: {
    getAll: (params) => axios.get(`${API}/tareas-despacho/ejecuciones`, { params }),
    getById: (id) => axios.get(`${API}/tareas-despacho/ejecuciones/${id}`),
    create: (data) => axios.post(`${API}/tareas-despacho/ejecuciones`, data),
    update: (id, data) => axios.put(`${API}/tareas-despacho/ejecuciones/${id}`, data),
    delete: (id) => axios.delete(`${API}/tareas-despacho/ejecuciones/${id}`),
    completar: (id, completada = true) => axios.patch(`${API}/tareas-despacho/ejecuciones/${id}/completar?completada=${completada}`),
  },
  
  // === TAREA PASTELITOS ===
  pastelitos: {
    getAll: (params) => axios.get(`${API}/tareas-despacho/pastelitos`, { params }),
    getById: (id) => axios.get(`${API}/tareas-despacho/pastelitos/${id}`),
    create: (data) => axios.post(`${API}/tareas-despacho/pastelitos`, data),
    update: (id, data) => axios.put(`${API}/tareas-despacho/pastelitos/${id}`, data),
    delete: (id) => axios.delete(`${API}/tareas-despacho/pastelitos/${id}`),
    completar: (id, completada = true) => axios.patch(`${API}/tareas-despacho/pastelitos/${id}/completar?completada=${completada}`),
    // Archivos
    getArchivos: (tareaId) => axios.get(`${API}/tareas-despacho/pastelitos/${tareaId}/archivos`),
    uploadArchivo: (tareaId, file) => {
      const formData = new FormData();
      formData.append('file', file);
      return axios.post(`${API}/tareas-despacho/pastelitos/${tareaId}/archivos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    deleteArchivo: (archivoId) => axios.delete(`${API}/tareas-despacho/pastelitos/archivos/${archivoId}`),
    crearActuacion: (tareaId) => axios.post(`${API}/tareas-despacho/pastelitos/${tareaId}/crear-actuacion`),
    crearActuacion: (tareaId) => axios.post(`${API}/tareas-despacho/pastelitos/${tareaId}/crear-actuacion`),
  },
  
  // === CITA CON ABOGADOS ===
  citasAbogados: {
    getAll: (params) => axios.get(`${API}/tareas-despacho/citas-abogados`, { params }),
    getById: (id) => axios.get(`${API}/tareas-despacho/citas-abogados/${id}`),
    create: (data) => axios.post(`${API}/tareas-despacho/citas-abogados`, data),
    update: (id, data) => axios.put(`${API}/tareas-despacho/citas-abogados/${id}`, data),
    delete: (id) => axios.delete(`${API}/tareas-despacho/citas-abogados/${id}`),
    cambiarEstado: (id, estado) => axios.patch(`${API}/tareas-despacho/citas-abogados/${id}/estado?estado=${estado}`),
    getStats: () => axios.get(`${API}/tareas-despacho/citas-abogados/stats/resumen`),
    getCanales: () => axios.get(`${API}/tareas-despacho/citas-abogados/canales`),
    getEstados: () => axios.get(`${API}/tareas-despacho/citas-abogados/estados`),
    getAbogados: () => axios.get(`${API}/tareas-despacho/citas-abogados/abogados-disponibles`),
    subirAdjunto: (citaId, formData) => axios.post(`${API}/tareas-despacho/citas-abogados/${citaId}/adjuntos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    eliminarAdjunto: (citaId, adjuntoIdx) => axios.delete(`${API}/tareas-despacho/citas-abogados/${citaId}/adjuntos/${adjuntoIdx}`),
  },
  
  // === SUB-TAREAS ===
  subtareas: {
    getByTarea: (tipoTarea, tareaId) => axios.get(`${API}/tareas-despacho/subtareas/${tipoTarea}/${tareaId}`),
    create: (data) => axios.post(`${API}/tareas-despacho/subtareas`, data),
    update: (id, data) => axios.put(`${API}/tareas-despacho/subtareas/${id}`, data),
    completar: (id, completada = true) => axios.post(`${API}/tareas-despacho/subtareas/${id}/completar?completada=${completada}`),
    delete: (id) => axios.delete(`${API}/tareas-despacho/subtareas/${id}`),
  },
  
  // Comentarios (genérico para todas las tareas)
  getComentarios: (tipoTarea, tareaId) => axios.get(`${API}/tareas-despacho/${tipoTarea}/${tareaId}/comentarios`),
  createComentario: (tipoTarea, tareaId, data) => axios.post(`${API}/tareas-despacho/${tipoTarea}/${tareaId}/comentarios`, data),
  deleteComentario: (tipoTarea, tareaId, comentarioId) => axios.delete(`${API}/tareas-despacho/${tipoTarea}/${tareaId}/comentarios/${comentarioId}`),
  
  // Estadísticas detalladas para gráficos
  getEstadisticasDetalladas: () => axios.get(`${API}/tareas-despacho/estadisticas-detalladas`),

  // Supervisión
  getSupervisionResumen: (params) => axios.get(`${API}/tareas-despacho/supervision/resumen`, { params }),
  getSupervisionUsuarioDetalle: (uid, params) => axios.get(`${API}/tareas-despacho/supervision/usuario/${uid}/detalle`, { params }),
  getSupervisionTareasPorUsuario: (params) => axios.get(`${API}/tareas-despacho/supervision/tareas-por-usuario`, { params }),
  getSupervisionActividadGrafico: (params) => axios.get(`${API}/tareas-despacho/supervision/actividad-usuario-grafico`, { params }),
};

// API de Atención al Cliente
export const atencionClienteAPI = {
  // Consultas
  getConsultas: (params) => axios.get(`${API}/atencion-cliente/consultas`, { params }),
  getConsulta: (id) => axios.get(`${API}/atencion-cliente/consultas/${id}`),
  createConsulta: (data) => axios.post(`${API}/atencion-cliente/consultas`, data),
  updateConsulta: (id, data) => axios.put(`${API}/atencion-cliente/consultas/${id}`, data),
  deleteConsulta: (id) => axios.delete(`${API}/atencion-cliente/consultas/${id}`),
  finalizarConsulta: (id, data) => axios.post(`${API}/atencion-cliente/consultas/${id}/finalizar`, data),
  
  // Seguimientos (hilo de tramitación)
  crearSeguimiento: (consultaId, data) => axios.post(`${API}/atencion-cliente/consultas/${consultaId}/seguimiento`, data),
  getHiloCompleto: (consultaId) => axios.get(`${API}/atencion-cliente/consultas/${consultaId}/hilo-completo`),
  
  // Respuestas
  createRespuesta: (consultaId, data) => axios.post(`${API}/atencion-cliente/consultas/${consultaId}/respuestas`, data),
  updateRespuesta: (consultaId, respuestaId, data) => axios.put(`${API}/atencion-cliente/consultas/${consultaId}/respuestas/${respuestaId}`, data),
  deleteRespuesta: (consultaId, respuestaId) => axios.delete(`${API}/atencion-cliente/consultas/${consultaId}/respuestas/${respuestaId}`),
  reenviarRespuesta: (consultaId, respuestaId) => axios.post(`${API}/atencion-cliente/consultas/${consultaId}/respuestas/${respuestaId}/reenviar`),
  
  // Adjuntos de Respuestas (para emails)
  subirAdjunto: (consultaId, formData) => axios.post(`${API}/atencion-cliente/consultas/${consultaId}/adjuntos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  listarAdjuntos: (consultaId) => axios.get(`${API}/atencion-cliente/consultas/${consultaId}/adjuntos`),
  
  // Adjuntos de Consultas (archivos asociados a la consulta)
  subirAdjuntoConsulta: (consultaId, formData) => axios.post(`${API}/atencion-cliente/consultas/${consultaId}/adjuntos-consulta`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  listarAdjuntosConsulta: (consultaId) => axios.get(`${API}/atencion-cliente/consultas/${consultaId}/adjuntos-consulta`),
  eliminarAdjuntoConsulta: (consultaId, path) => axios.delete(`${API}/atencion-cliente/consultas/${consultaId}/adjuntos-consulta`, { 
    data: { path } 
  }),
  
  // Crear actuación en expediente
  crearActuacion: (consultaId, data) => axios.post(`${API}/atencion-cliente/consultas/${consultaId}/crear-actuacion`, data),
  
  // Plantillas
  getPlantillas: (activasOnly = false) => axios.get(`${API}/atencion-cliente/plantillas`, { params: { activas_only: activasOnly } }),
  createPlantilla: (data) => axios.post(`${API}/atencion-cliente/plantillas`, data),
  updatePlantilla: (id, data) => axios.put(`${API}/atencion-cliente/plantillas/${id}`, data),
  deletePlantilla: (id) => axios.delete(`${API}/atencion-cliente/plantillas/${id}`),
  
  // Configuración Email
  getConfigEmail: () => axios.get(`${API}/atencion-cliente/config-email`),
  getDefaultConfig: () => axios.get(`${API}/atencion-cliente/config-email/default`),
  createConfigEmail: (data) => axios.post(`${API}/atencion-cliente/config-email`, data),
  updateConfigEmail: (id, data) => axios.put(`${API}/atencion-cliente/config-email/${id}`, data),
  testConfigEmail: (id) => axios.post(`${API}/atencion-cliente/config-email/${id}/test`),
  deleteConfigEmail: (id) => axios.delete(`${API}/atencion-cliente/config-email/${id}`),
  
  // Búsqueda
  buscarExpedientes: (query) => axios.get(`${API}/atencion-cliente/buscar-expedientes`, { params: { query } }),
  buscarContactos: (query) => axios.get(`${API}/atencion-cliente/buscar-contactos`, { params: { query } }),
};

// API de Reclamaciones Extrajudiciales
export const extrajudicialAPI = {
  // Config
  getEstados: () => axios.get(`${API}/extrajudicial/config/estados`),
  getResultados: () => axios.get(`${API}/extrajudicial/config/resultados`),
  getTiposConcepto: () => axios.get(`${API}/extrajudicial/config/tipos-concepto`),
  getTiposDocumento: () => axios.get(`${API}/extrajudicial/config/tipos-documento`),
  getTasasInteres: () => axios.get(`${API}/extrajudicial/config/tasas-interes`),
  
  // Cálculos
  calcularIntereses: (importe, fechaDesde, fechaHasta = null) => 
    axios.post(`${API}/extrajudicial/calcular/intereses?importe=${importe}&fecha_desde=${fechaDesde}${fechaHasta ? `&fecha_hasta=${fechaHasta}` : ''}`),
  calcularConcepto: (tipo, importePagado, fechaPago = null) => 
    axios.post(`${API}/extrajudicial/calcular/concepto?tipo=${tipo}&importe_pagado=${importePagado}${fechaPago ? `&fecha_pago=${fechaPago}` : ''}`),
  
  // CRUD Reclamaciones
  getAll: (params) => axios.get(`${API}/extrajudicial/reclamaciones`, { params }),
  getStats: () => axios.get(`${API}/extrajudicial/reclamaciones/stats`),
  getById: (id) => axios.get(`${API}/extrajudicial/reclamaciones/${id}`),
  create: (data) => axios.post(`${API}/extrajudicial/reclamaciones`, data),
  delete: (id) => axios.delete(`${API}/extrajudicial/reclamaciones/${id}`),
  
  // Actualizar datos
  updateDatosCliente: (id, data) => axios.put(`${API}/extrajudicial/reclamaciones/${id}/datos-cliente`, data),
  updateDatosHipoteca: (id, data) => axios.put(`${API}/extrajudicial/reclamaciones/${id}/datos-hipoteca`, data),
  updateClausulas: (id, data) => axios.put(`${API}/extrajudicial/reclamaciones/${id}/clausulas`, data),
  updateNotas: (id, notas) => axios.put(`${API}/extrajudicial/reclamaciones/${id}/notas?notas=${encodeURIComponent(notas)}`),
  
  // Conceptos
  addConcepto: (reclamacionId, data) => axios.post(`${API}/extrajudicial/reclamaciones/${reclamacionId}/conceptos`, data),
  updateConcepto: (reclamacionId, conceptoId, data) => axios.put(`${API}/extrajudicial/reclamaciones/${reclamacionId}/conceptos/${conceptoId}`, data),
  deleteConcepto: (reclamacionId, conceptoId) => axios.delete(`${API}/extrajudicial/reclamaciones/${reclamacionId}/conceptos/${conceptoId}`),
  
  // Documentos
  uploadDocumento: (reclamacionId, formData) => axios.post(`${API}/extrajudicial/reclamaciones/${reclamacionId}/documentos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadDocumento: (reclamacionId, documentoId) => axios.get(`${API}/extrajudicial/reclamaciones/${reclamacionId}/documentos/${documentoId}/download`, { responseType: 'blob' }),
  previewDocumento: (reclamacionId, documentoId) => `${API}/extrajudicial/reclamaciones/${reclamacionId}/documentos/${documentoId}/preview`,
  deleteDocumento: (reclamacionId, documentoId) => axios.delete(`${API}/extrajudicial/reclamaciones/${reclamacionId}/documentos/${documentoId}`),
  
  // Estado
  cambiarEstado: (id, data) => axios.post(`${API}/extrajudicial/reclamaciones/${id}/cambiar-estado`, data),
  
  // Seguimiento - Flujo completo
  registrarFirma: (id, data) => axios.post(`${API}/extrajudicial/reclamaciones/${id}/registrar-firma`, data),
  registrarEnvio: (id, data) => axios.post(`${API}/extrajudicial/reclamaciones/${id}/registrar-envio`, data),
  registrarRespuesta: (id, data) => axios.post(`${API}/extrajudicial/reclamaciones/${id}/registrar-respuesta`, data),
  adjuntarDocumentoSeguimiento: (id, tipoSeguimiento, archivo) => {
    const formData = new FormData();
    formData.append('tipo_seguimiento', tipoSeguimiento);
    formData.append('archivo', archivo);
    return axios.post(`${API}/extrajudicial/reclamaciones/${id}/adjuntar-documento-seguimiento`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Generación de documentos
  generarDocumento: (id, tipoDocumento) => axios.post(`${API}/extrajudicial/reclamaciones/${id}/generar-documento`, { tipo_documento: tipoDocumento }),
  generarResumenPDF: (id) => axios.post(`${API}/extrajudicial/reclamaciones/${id}/generar-resumen-pdf`),
  
  // Conversión a expediente
  convertirAExpediente: (id) => axios.post(`${API}/extrajudicial/reclamaciones/${id}/convertir-expediente`),
  resetPaso: (id, paso) => axios.post(`${API}/extrajudicial/reclamaciones/${id}/reset-paso`, { paso }),  // v106
  buscarExpedientes: (q) => axios.get(`${API}/extrajudicial/buscar-expedientes`, { params: { q } }),  // v109 (fix v107)
  asociarExpediente: (id, expediente_id) => axios.post(`${API}/extrajudicial/reclamaciones/${id}/asociar-expediente`, { expediente_id }),  // v109 (fix v107)
  crearObservacionGestion: (id, descripcion) => axios.post(`${API}/extrajudicial/reclamaciones/${id}/observacion-gestion`, { descripcion }),
  listarObservacionesGestion: (id) => axios.get(`${API}/extrajudicial/reclamaciones/${id}/observaciones-gestion`),
  
  // Catálogo de cláusulas (configuración)
  getCatalogoClausulas: (incluirInactivas = false) => axios.get(`${API}/extrajudicial/config/catalogo-clausulas?incluir_inactivas=${incluirInactivas}`),
  crearClausulaCatalogo: (data) => axios.post(`${API}/extrajudicial/config/catalogo-clausulas`, data),
  actualizarClausulaCatalogo: (id, data) => axios.put(`${API}/extrajudicial/config/catalogo-clausulas/${id}`, data),
  eliminarClausulaCatalogo: (id) => axios.delete(`${API}/extrajudicial/config/catalogo-clausulas/${id}`),
  
  // Plantillas de documentos (configuración)
  getPlantillas: (tipo = null) => axios.get(`${API}/extrajudicial/config/plantillas`, { params: { tipo } }),
  uploadPlantilla: (formData) => axios.post(`${API}/extrajudicial/config/plantillas`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deletePlantilla: (id) => axios.delete(`${API}/extrajudicial/config/plantillas/${id}`),
  downloadPlantilla: (id) => axios.get(`${API}/extrajudicial/config/plantillas/${id}/descargar`, { responseType: 'blob' }),
  argumentosCatalogo: (rid) => axios.get(`${API}/extrajudicial/reclamaciones/${rid}/argumentos/catalogo`),
  argumentosList: (rid) => axios.get(`${API}/extrajudicial/reclamaciones/${rid}/argumentos`),
  argumentoSugerir: (rid, data) => axios.post(`${API}/extrajudicial/reclamaciones/${rid}/argumentos/sugerir`, data),
  argumentoGuardar: (rid, ck, data) => axios.put(`${API}/extrajudicial/reclamaciones/${rid}/argumentos/${ck}`, data),
  argumentoBorrar: (rid, ck) => axios.delete(`${API}/extrajudicial/reclamaciones/${rid}/argumentos/${ck}`),
  iaProviders: () => axios.get(`${API}/hipotecas/providers`),
  respuestasList: (rid) => axios.get(`${API}/extrajudicial/reclamaciones/${rid}/respuestas`),
  respuestasRevisar: (rid, data) => axios.post(`${API}/extrajudicial/reclamaciones/${rid}/respuestas/revisar`, data || {}),
  respuestasSincronizar: (data) => axios.post(`${API}/extrajudicial/respuestas/sincronizar`, data || {}),
  imapEstado: () => axios.get(`${API}/extrajudicial/respuestas/imap-estado`),
  bandejaImap: (params) => axios.get(`${API}/extrajudicial/respuestas/bandeja`, { params }),
  bandejaAdjunto: (params) => axios.get(`${API}/extrajudicial/respuestas/bandeja/adjunto`, { params, responseType: 'blob' }),
  respuestaVincular: (rid, data) => axios.post(`${API}/extrajudicial/reclamaciones/${rid}/respuestas/vincular`, data),
  respuestaEditar: (rid, id, data) => axios.put(`${API}/extrajudicial/reclamaciones/${rid}/respuestas/${id}`, data),
  respuestaReanalizar: (rid, id, data) => axios.post(`${API}/extrajudicial/reclamaciones/${rid}/respuestas/${id}/reanalizar`, data || {}),
  respuestaEliminar: (rid, id) => axios.delete(`${API}/extrajudicial/reclamaciones/${rid}/respuestas/${id}`),
};

// =============================================================================
// ESTAFAS BANCARIAS - Módulo de reclamaciones por fraude
// =============================================================================
export const estafasBancariasAPI = {
  // Configuración
  getConfig: () => axios.get(`${API}/estafas-bancarias/config`),
  
  // CRUD Casos
  getAll: (params) => axios.get(`${API}/estafas-bancarias`, { params }),
  getStats: () => axios.get(`${API}/estafas-bancarias/stats`),
  getById: (id) => axios.get(`${API}/estafas-bancarias/${id}`),
  create: (data) => axios.post(`${API}/estafas-bancarias`, data),
  update: (id, data) => axios.put(`${API}/estafas-bancarias/${id}`, data),
  delete: (id) => axios.delete(`${API}/estafas-bancarias/${id}`),
  
  // Pasos del flujo
  updatePaso: (id, data) => axios.post(`${API}/estafas-bancarias/${id}/paso`, data),
  
  // Documentos
  uploadDocumento: (casoId, formData) => axios.post(`${API}/estafas-bancarias/${casoId}/documentos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadDocumento: (casoId, docId) => axios.get(`${API}/estafas-bancarias/${casoId}/documentos/${docId}/descargar`, { responseType: 'blob' }),
  deleteDocumento: (casoId, docId) => axios.delete(`${API}/estafas-bancarias/${casoId}/documentos/${docId}`),
  
  // Generación de documentos
  generarDocumento: (casoId, tipo, formato = 'word') => axios.post(`${API}/estafas-bancarias/${casoId}/generar-documento?tipo=${tipo}&formato=${formato}`),
  
  // Envío y respuesta del banco
  registrarEnvio: (casoId, formData) => axios.post(`${API}/estafas-bancarias/${casoId}/registrar-envio`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  registrarRespuesta: (casoId, formData) => axios.post(`${API}/estafas-bancarias/${casoId}/registrar-respuesta`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Conversión a expediente
  crearExpediente: (casoId) => axios.post(`${API}/estafas-bancarias/${casoId}/crear-expediente`),
  
  // Plantillas
  getPlantillas: (tipo = null) => axios.get(`${API}/estafas-bancarias/plantillas`, { params: { tipo } }),
  uploadPlantilla: (formData) => axios.post(`${API}/estafas-bancarias/plantillas`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deletePlantilla: (id) => axios.delete(`${API}/estafas-bancarias/plantillas/${id}`),
};

// =============================================================================
// CORREO - Gmail Integration
// =============================================================================
export const correoAPI = {
  // Cuentas de correo
  getCuentas: () => axios.get(`${API}/correo/cuentas`),
  getCuenta: (id) => axios.get(`${API}/correo/cuentas/${id}`),
  crearCuenta: (data) => axios.post(`${API}/correo/cuentas`, data),
  actualizarCuenta: (id, data) => axios.put(`${API}/correo/cuentas/${id}`, data),
  eliminarCuenta: (id) => axios.delete(`${API}/correo/cuentas/${id}`),
  
  // OAuth
  iniciarAutorizacion: (cuentaId) => axios.get(`${API}/correo/cuentas/${cuentaId}/authorize`),
  revocarAutorizacion: (cuentaId) => axios.post(`${API}/correo/cuentas/${cuentaId}/revoke`),
  
  // Emails
  getEmails: (cuentaId, maxResults = 20, pageToken = null, q = null) => {
    const params = new URLSearchParams();
    params.append('max_results', maxResults);
    if (pageToken) params.append('page_token', pageToken);
    if (q) params.append('q', q);
    return axios.get(`${API}/correo/cuentas/${cuentaId}/emails?${params.toString()}`);
  },
  getEmail: (cuentaId, messageId) => axios.get(`${API}/correo/cuentas/${cuentaId}/emails/${messageId}`),
  enviarEmail: (cuentaId, destinatario, asunto, cuerpo, replyToMessageId = null) => {
    const params = new URLSearchParams();
    params.append('destinatario', destinatario);
    params.append('asunto', asunto);
    params.append('cuerpo', cuerpo);
    if (replyToMessageId) params.append('reply_to_message_id', replyToMessageId);
    return axios.post(`${API}/correo/cuentas/${cuentaId}/send?${params.toString()}`);
  },
  
  // Acciones sobre emails
  archivarEmails: (cuentaId, messageIds) => axios.post(`${API}/correo/cuentas/${cuentaId}/emails/archivar`, { message_ids: messageIds }),
  guardarEmails: (cuentaId, messageIds, carpeta = 'guardados') => axios.post(`${API}/correo/cuentas/${cuentaId}/emails/guardar`, { message_ids: messageIds, carpeta }),
  marcarLeidos: (cuentaId, messageIds) => axios.post(`${API}/correo/cuentas/${cuentaId}/emails/marcar-leido`, { message_ids: messageIds }),
  eliminarEmail: (cuentaId, messageId) => axios.delete(`${API}/correo/cuentas/${cuentaId}/emails/${messageId}`),
  
  // Tareas de atención
  getTareas: (estado = null, cuentaId = null) => {
    const params = new URLSearchParams();
    if (estado) params.append('estado', estado);
    if (cuentaId) params.append('cuenta_id', cuentaId);
    return axios.get(`${API}/correo/tareas?${params.toString()}`);
  },
  getTarea: (tareaId) => axios.get(`${API}/correo/tareas/${tareaId}`),
  crearTarea: (cuentaId, data) => axios.post(`${API}/correo/cuentas/${cuentaId}/crear-tarea`, data),
  actualizarTarea: (tareaId, data) => {
    const params = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) params.append(key, value);
    });
    return axios.put(`${API}/correo/tareas/${tareaId}?${params.toString()}`);
  },
  responderTarea: (tareaId, data) => axios.post(`${API}/correo/tareas/${tareaId}/responder`, data),
  crearActuacion: (tareaId, tipo = 'Atención cliente', descripcion = null) => {
    const params = new URLSearchParams();
    params.append('tipo', tipo);
    if (descripcion) params.append('descripcion', descripcion);
    return axios.post(`${API}/correo/tareas/${tareaId}/crear-actuacion?${params.toString()}`);
  }
};

// =============================================================================
// BACKUPS - Copias de Seguridad
// =============================================================================
export const backupsAPI = {
  list: () => axios.get(`${API}/backups/list`),
  create: (includeUploads = true) => axios.post(`${API}/backups/create?include_uploads=${includeUploads}`),
  download: (backupId) => axios.get(`${API}/backups/download/${backupId}`, { responseType: 'blob' }),
  delete: (backupId) => axios.delete(`${API}/backups/${backupId}`),
  getConfig: () => axios.get(`${API}/backups/config`),
  updateConfig: (config) => axios.put(`${API}/backups/config`, config),
  toggleAuto: () => axios.post(`${API}/backups/toggle-auto`),
  getStats: () => axios.get(`${API}/backups/stats`),
  runNow: () => axios.post(`${API}/backups/run-now`),
};

// =============================================================================
// PAGOS - Gestión de pagos de expedientes
// =============================================================================
export const pagosAPI = {
  // Conceptos de pago
  getConceptos: (expedienteId, incluirInactivos = false) => 
    axios.get(`${API}/expedientes/${expedienteId}/pagos/conceptos`, { params: { incluir_inactivos: incluirInactivos } }),
  createConcepto: (expedienteId, data) => 
    axios.post(`${API}/expedientes/${expedienteId}/pagos/conceptos`, data),
  updateConcepto: (expedienteId, conceptoId, data) => 
    axios.put(`${API}/expedientes/${expedienteId}/pagos/conceptos/${conceptoId}`, data),
  deleteConcepto: (expedienteId, conceptoId) => 
    axios.delete(`${API}/expedientes/${expedienteId}/pagos/conceptos/${conceptoId}`),
  
  // Pagos
  createPago: (expedienteId, conceptoId, data) => 
    axios.post(`${API}/expedientes/${expedienteId}/pagos/conceptos/${conceptoId}/pagos`, data),
  deletePago: (expedienteId, pagoId) => 
    axios.delete(`${API}/expedientes/${expedienteId}/pagos/${pagoId}`),
  
  // Resumen y tipos
  getResumen: (expedienteId) => axios.get(`${API}/expedientes/${expedienteId}/pagos/resumen`),
  getTiposDisponibles: (expedienteId) => axios.get(`${API}/expedientes/${expedienteId}/pagos/tipos-disponibles`),
  
  // Supervisión
  getPagosPendientes: (tiposFiltro = null) => {
    const params = {};
    if (tiposFiltro && tiposFiltro.length > 0) {
      params.tipos = tiposFiltro.join(',');
    }
    return axios.get(`${API}/expedientes/supervision/pagos-pendientes`, { params });
  },
  getPagosResumen: () => axios.get(`${API}/expedientes/supervision/pagos-resumen`),
};

// =============================================================================
// PLANTILLAS - Gestión de plantillas de documentos
// =============================================================================
export const plantillasAPI = {
  // Carpetas
  getCarpetas: (params) => axios.get(`${API}/plantillas/carpetas`, { params }),
  getCarpeta: (id) => axios.get(`${API}/plantillas/carpetas/${id}`),
  createCarpeta: (data) => axios.post(`${API}/plantillas/carpetas`, data),
  updateCarpeta: (id, data) => axios.put(`${API}/plantillas/carpetas/${id}`, data),
  deleteCarpeta: (id) => axios.delete(`${API}/plantillas/carpetas/${id}`),
  
  // Plantillas
  getAll: (params) => axios.get(`${API}/plantillas/`, { params }),
  getById: (id) => axios.get(`${API}/plantillas/${id}`),
  create: (data) => axios.post(`${API}/plantillas/`, data),
  update: (id, data) => axios.put(`${API}/plantillas/${id}`, data),
  delete: (id) => axios.delete(`${API}/plantillas/${id}`),
  uploadArchivo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/plantillas/${id}/archivo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getDownloadUrl: (id) => `${API}/plantillas/${id}/descargar`,
  
  // Inicialización
  inicializarCarpetas: () => axios.post(`${API}/plantillas/inicializar-carpetas`),
};

// ============================================================================
// PLANTILLAS WORD - Generación automática de escritos judiciales
// ============================================================================
export const plantillasWordAPI = {
  getAll: (params) => axios.get(`${API}/plantillas-word/`, { params }),
  getVariables: () => axios.get(`${API}/plantillas-word/variables`),
  crear: (formData) => axios.post(`${API}/plantillas-word/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  actualizar: (id, formData) => axios.put(`${API}/plantillas-word/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  eliminar: (id) => axios.delete(`${API}/plantillas-word/${id}`),
  previewDatos: (expedienteId, procedimientoId) => axios.get(
    `${API}/plantillas-word/preview-datos/${expedienteId}`,
    { params: (procedimientoId !== null && procedimientoId !== undefined) ? { procedimiento_id: procedimientoId } : {} }
  ),
  generar: (plantillaId, expedienteId, procedimientoId) => axios.post(
    `${API}/plantillas-word/generar?plantilla_id=${plantillaId}&expediente_id=${expedienteId}${(procedimientoId !== null && procedimientoId !== undefined) ? `&procedimiento_id=${procedimientoId}` : ''}`,
    null,
    { responseType: 'blob' }
  ),
  descargar: (id) => axios.get(`${API}/plantillas-word/${id}/descargar`, { responseType: 'blob' }),
  // Carpetas
  getCarpetas: () => axios.get(`${API}/plantillas-word/carpetas`),
  crearCarpeta: (formData) => axios.post(`${API}/plantillas-word/carpetas`, formData),
  actualizarCarpeta: (id, formData) => axios.put(`${API}/plantillas-word/carpetas/${id}`, formData),
  eliminarCarpeta: (id) => axios.delete(`${API}/plantillas-word/carpetas/${id}`),
};

// ============================================================================
// EMPRESA / DESPACHO
// ============================================================================
export const empresaAPI = {
  // Datos generales
  get: () => axios.get(`${API}/empresa/`),
  update: (data) => axios.put(`${API}/empresa/`, data),
  
  // Direcciones
  addDireccion: (direccion) => axios.post(`${API}/empresa/direcciones`, direccion),
  deleteDireccion: (index) => axios.delete(`${API}/empresa/direcciones/${index}`),
  
  // Teléfonos
  addTelefono: (telefono) => axios.post(`${API}/empresa/telefonos`, telefono),
  deleteTelefono: (index) => axios.delete(`${API}/empresa/telefonos/${index}`),
};

// ============================================================================
// IMPORTACIÓN DE DATOS
// ============================================================================
export const importacionAPI = {
  // Subir archivos y analizar
  subir: (archivos) => {
    const formData = new FormData();
    archivos.forEach(archivo => formData.append('archivos', archivo));
    return axios.post(`${API}/importacion/subir`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Obtener sesión
  getSesion: (sessionId) => axios.get(`${API}/importacion/sesion/${sessionId}`),
  
  // Ejecutar importación
  ejecutar: (data) => axios.post(`${API}/importacion/ejecutar`, data),
  
  // Vista previa de datos
  preview: (sessionId, entidad, limit = 10) => 
    axios.get(`${API}/importacion/preview/${sessionId}/${entidad}?limit=${limit}`),
  
  // Entidades destino disponibles
  getEntidadesDestino: () => axios.get(`${API}/importacion/entidades-destino`),
  
  // Eliminar sesión
  deleteSesion: (sessionId) => axios.delete(`${API}/importacion/sesion/${sessionId}`),
};

// ============================================================================
// ACTUALIZACIONES DEL SISTEMA
// ============================================================================
export const actualizacionesAPI = {
  // Analizar paquete de actualización
  analizar: (formData) => axios.post(`${API}/actualizaciones/analizar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Aplicar actualización
  aplicar: (formData, crearBackup = true) => axios.post(
    `${API}/actualizaciones/aplicar?crear_backup=${crearBackup}`, 
    formData, 
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  
  // Historial de actualizaciones
  getHistorial: () => axios.get(`${API}/actualizaciones/historial`),
  
  // Versión actual
  getVersionActual: () => axios.get(`${API}/actualizaciones/version-actual`),
  
  // Limpiar archivos temporales
  limpiarTemporales: () => axios.delete(`${API}/actualizaciones/limpiar-temporales`),
  
  // Diagnóstico del sistema
  getDiagnostico: () => axios.get(`${API}/actualizaciones/diagnostico`),
  
  // Listar backups disponibles
  getBackups: () => axios.get(`${API}/actualizaciones/listar-backups`),
  
  // Restaurar backup
  restaurarBackup: (nombreBackup) => axios.post(`${API}/actualizaciones/restaurar-backup/${nombreBackup}`),
  
  // Generar paquete de actualización (para desarrollo)
  generarPaquete: (datos) => axios.post(`${API}/actualizaciones/generar-paquete`, null, { params: datos }),
  
  // Listar paquetes generados
  getPaquetes: () => axios.get(`${API}/actualizaciones/listar-paquetes`),
  
  // Descargar paquete
  descargarPaquete: (nombre) => `${API}/actualizaciones/descargar-paquete/${nombre}`,
};

// ============================================================================
// GESTIÓN DE DATOS (Borrado y Exportación)
// ============================================================================
export const gestionDatosAPI = {
  // Obtener entidades disponibles
  getEntidades: () => axios.get(`${API}/gestion-datos/entidades`),
  
  // Obtener estadísticas de entidades
  getEstadisticas: (entidades) => axios.post(`${API}/gestion-datos/estadisticas`, entidades),
  
  // Solicitar código de borrado (Paso 1)
  solicitarBorrado: (entidades) => axios.post(`${API}/gestion-datos/solicitar-borrado`, entidades),
  
  // Confirmar borrado (Paso 2)
  confirmarBorrado: (data) => axios.post(`${API}/gestion-datos/confirmar-borrado`, data),
  
  // Exportar datos
  exportar: (entidades, formato) => axios.post(`${API}/gestion-datos/exportar`, 
    { entidades, formato },
    { responseType: 'blob' }
  ),
  
  // Listar backups
  getBackups: () => axios.get(`${API}/gestion-datos/backups`),
};

// ============================================================================
// CHAT LEGAL AVANZADO (Análisis de Expedientes)
// ============================================================================
export const chatLegalAPI = {
  // Listar expedientes disponibles
  getExpedientes: () => axios.get(`${API}/chat-legal/expedientes`),
  
  // Obtener contexto completo de un expediente
  getExpedienteContext: (id) => axios.get(`${API}/chat-legal/expediente/${id}`),
  
  // Obtener plazos críticos
  getPlazosCriticos: (dias = 30) => axios.get(`${API}/chat-legal/plazos-criticos?dias=${dias}`),
  
  // Analizar con IA
  analizar: (data) => axios.post(`${API}/chat-legal/analizar`, data),
  
  // Resumir actuaciones
  resumirActuaciones: (expedienteId) => axios.post(`${API}/chat-legal/resumir-actuaciones?expediente_id=${expedienteId}`),
  
  // Analizar probabilidad de éxito
  analizarProbabilidad: (expedienteId) => axios.post(`${API}/chat-legal/analizar-probabilidad?expediente_id=${expedienteId}`),
  
  // Sugerir estrategia
  sugerirEstrategia: (expedienteId) => axios.post(`${API}/chat-legal/sugerir-estrategia?expediente_id=${expedienteId}`),
};

// ============================================================================
// PORTAL DEL CLIENTE
// ============================================================================
export const portalClienteAPI = {
  // Autenticación
  login: (data) => axios.post(`${API}/portal-cliente/login`, data),
  registrar: (data) => axios.post(`${API}/portal-cliente/registrar`, data),
  
  // Gestión de clientes (admin)
  getClientes: () => axios.get(`${API}/portal-cliente/clientes`),
  toggleCliente: (clienteId) => axios.put(`${API}/portal-cliente/clientes/${clienteId}/toggle`),
  regenerarCodigo: (clienteId) => axios.post(`${API}/portal-cliente/clientes/${clienteId}/regenerar-codigo`),
  
  // Vista cliente
  getMisExpedientes: (email) => axios.get(`${API}/portal-cliente/mis-expedientes?cliente_email=${email}`),
  getExpediente: (expId, email) => axios.get(`${API}/portal-cliente/expediente/${expId}?cliente_email=${email}`),
  
  // Mensajería
  getMensajes: (expId, email) => axios.get(`${API}/portal-cliente/mensajes/${expId}?cliente_email=${email}`),
  enviarMensaje: (email, data) => axios.post(`${API}/portal-cliente/mensajes?cliente_email=${email}`, data),
  enviarMensajeAbogado: (expId, contenido, abogadoEmail) => 
    axios.post(`${API}/portal-cliente/mensajes/abogado?expediente_id=${expId}&contenido=${encodeURIComponent(contenido)}&abogado_email=${abogadoEmail}`),
  
  // Documentos
  getDocumentos: (expId, email) => axios.get(`${API}/portal-cliente/documentos/${expId}?cliente_email=${email}`),
  
  // Gestión de accesos
  asignarExpediente: (clienteId, expId, permisos = {}) => 
    axios.post(`${API}/portal-cliente/clientes/${clienteId}/expedientes/${expId}`, null, { params: permisos }),
  revocarExpediente: (clienteId, expId) => 
    axios.delete(`${API}/portal-cliente/clientes/${clienteId}/expedientes/${expId}`),
};

// ============================================================================
// COLABORACIÓN
// ============================================================================
export const colaboracionAPI = {
  // Comentarios
  crearComentario: (data) => axios.post(`${API}/colaboracion/comentarios`, data),
  getComentarios: (expedienteId) => axios.get(`${API}/colaboracion/comentarios/${expedienteId}`),
  
  // Historial
  getHistorial: (entidad, entidadId) => axios.get(`${API}/colaboracion/historial/${entidad}/${entidadId}`),
  
  // Notificaciones
  getNotificaciones: () => axios.get(`${API}/colaboracion/notificaciones`),
  marcarLeida: (id) => axios.put(`${API}/colaboracion/notificaciones/${id}/leer`),
  marcarTodasLeidas: () => axios.put(`${API}/colaboracion/notificaciones/leer-todas`),
  
  // Tareas con workflow
  asignarTarea: (data) => axios.post(`${API}/colaboracion/tareas/asignar`, data),
  cambiarEstadoTarea: (tareaId, estado) => axios.put(`${API}/colaboracion/tareas/${tareaId}/estado?nuevo_estado=${estado}`),
};

// ============================================================================
// DOCUMENTOS AUTOMÁTICOS
// ============================================================================
export const documentosAutoAPI = {
  // Placeholders
  getPlaceholders: () => axios.get(`${API}/documentos-auto/placeholders`),
  
  // Generación
  generar: (data) => axios.post(`${API}/documentos-auto/generar`, data),
  preview: (plantillaId, expedienteId) => axios.post(`${API}/documentos-auto/preview?plantilla_id=${plantillaId}&expediente_id=${expedienteId}`),
  
  // OCR
  ocr: (formData, idioma = 'spa') => axios.post(`${API}/documentos-auto/ocr?idioma=${idioma}`, formData),
  getIdiomasOCR: () => axios.get(`${API}/documentos-auto/ocr/idiomas`),
};

// ============================================================================
// AUDITORÍA RGPD
// ============================================================================
export const auditoriaAPI = {
  getLogs: (params = {}) => axios.get(`${API}/auditoria/logs`, { params }),
  getEstadisticas: (dias = 30) => axios.get(`${API}/auditoria/estadisticas?dias=${dias}`),
  exportar: (formato, params = {}) => axios.get(`${API}/auditoria/exportar?formato=${formato}`, { params, responseType: 'blob' }),
  getHistorialUsuario: (usuarioId) => axios.get(`${API}/auditoria/usuario/${usuarioId}`),
  eliminarDatosUsuario: (usuarioId, confirmar = false) => axios.delete(`${API}/auditoria/usuario/${usuarioId}?confirmar=${confirmar}`),
  getAcciones: () => axios.get(`${API}/auditoria/acciones`),
};

// ============================================================================
// SENTENCIAS (Fondo Jurisprudencial)
// ============================================================================
export const sentenciasAPI = {
  // Categorías
  getCategorias: () => axios.get(`${API}/sentencias/categorias`),
  crearCategoria: (data) => axios.post(`${API}/sentencias/categorias`, data),
  inicializarCategorias: () => axios.post(`${API}/sentencias/categorias/inicializar`),
  
  // Sentencias CRUD
  getAll: (params) => axios.get(`${API}/sentencias`, { params }),
  getById: (id) => axios.get(`${API}/sentencias/${id}`),
  upload: (formData) => axios.post(`${API}/sentencias/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => axios.put(`${API}/sentencias/${id}`, data),
  delete: (id) => axios.delete(`${API}/sentencias/${id}`),
  descargar: (id) => `${API}/sentencias/${id}/descargar`,
  
  // IA
  generarResumen: (id) => axios.post(`${API}/sentencias/${id}/generar-resumen`),
  consultar: (data) => axios.post(`${API}/sentencias/ia/consultar`, data),
  analizarMultiples: (data) => axios.post(`${API}/sentencias/ia/analizar-multiples`, data),
  generarEscrito: (formData) => axios.post(`${API}/sentencias/ia/generar-escrito`, formData),
  
  // Estadísticas
  getEstadisticas: () => axios.get(`${API}/sentencias/estadisticas/resumen`),
};

// ============================================================================
// AI SETTINGS EXPEDIENTES
// ============================================================================
export const aiExpedientesAPI = {
  getSettings: () => axios.get(`${API}/ai-settings/expedientes`),
  updateSettings: (data) => axios.put(`${API}/ai-settings/expedientes`, data),
  testConnection: (data) => axios.post(`${API}/ai-settings/expedientes/test`, data),
};

// ============================================================================
// AUDITORÍA DE EXPEDIENTES (Control Excel)
// ============================================================================
export const auditoriaExpedientesAPI = {
  // Dashboard
  getDashboard: (params) => axios.get(`${API}/auditoria-expedientes/dashboard`, { params }),
  
  // Sesiones
  getSesiones: (params) => axios.get(`${API}/auditoria-expedientes/sesiones`, { params }),
  getSesion: (id) => axios.get(`${API}/auditoria-expedientes/sesiones/${id}`),
  updateSesion: (id, data) => axios.put(`${API}/auditoria-expedientes/sesiones/${id}`, data),
  deleteSesion: (id) => axios.delete(`${API}/auditoria-expedientes/sesiones/${id}`),
  
  // Subir Excel
  subirExcel: (file, nombreSesion = null, pestana = 'acabados 2025') => {
    const formData = new FormData();
    formData.append('file', file);
    if (nombreSesion) formData.append('nombre_sesion', nombreSesion);
    if (pestana) formData.append('pestana', pestana);
    return axios.post(`${API}/auditoria-expedientes/subir-excel`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Items
  aprobarItem: (itemId) => axios.put(`${API}/auditoria-expedientes/items/${itemId}/aprobar`),
  rechazarItem: (itemId, notas = null) => axios.put(`${API}/auditoria-expedientes/items/${itemId}/rechazar`, null, { params: { notas } }),
  
  // Aplicar cambios
  aplicarItems: (sesionId, data) => axios.post(`${API}/auditoria-expedientes/sesiones/${sesionId}/aplicar`, data),
  
  // Cláusulas de expediente
  getClausulas: (expedienteId) => axios.get(`${API}/auditoria-expedientes/expedientes/${expedienteId}/clausulas`),
  createClausula: (expedienteId, data) => axios.post(`${API}/auditoria-expedientes/expedientes/${expedienteId}/clausulas`, data),
  updateClausula: (expedienteId, clausulaId, data) => axios.put(`${API}/auditoria-expedientes/expedientes/${expedienteId}/clausulas/${clausulaId}`, data),
  deleteClausula: (expedienteId, clausulaId) => axios.delete(`${API}/auditoria-expedientes/expedientes/${expedienteId}/clausulas/${clausulaId}`),
  
  // Configuración
  getTiposClausulas: () => axios.get(`${API}/auditoria-expedientes/tipos-clausulas`),
  getEstadosClausulas: () => axios.get(`${API}/auditoria-expedientes/estados-clausulas`),
};

// Zapier - Configuración y Logs
export const zapierAPI = {
  // Configuración
  getConfig: () => axios.get(`${API}/configuracion/zapier/config`),
  regenerarKey: () => axios.post(`${API}/configuracion/zapier/regenerar-key`),
  testConexion: () => axios.post(`${API}/configuracion/zapier/test-conexion`),
  
  // Estadísticas
  getStats: () => axios.get(`${API}/configuracion/zapier/stats`),
  
  // Logs
  getLogs: (params) => axios.get(`${API}/configuracion/zapier/logs`, { params }),
  getLogDetail: (logId) => axios.get(`${API}/configuracion/zapier/log/${logId}`),
};

// ============================================================================
// DEFAULT EXPORT (para compatibilidad con import api from '../api')
// ============================================================================
const api = {
  get: axios.get,
  post: axios.post,
  put: axios.put,
  delete: axios.delete,
  patch: axios.patch,
};

export default api;




// COBRETTIS API - COMPLETO
export const cobrettisAPI = {
  getDashboard: () => axios.get(`${API}/cobrettis/dashboard`),
  getStatsResumen: () => axios.get(`${API}/cobrettis/stats/resumen`),
  getAll: (params) => axios.get(`${API}/cobrettis`, { params }),
  getById: (id) => axios.get(`${API}/cobrettis/${id}`),
  create: (data) => axios.post(`${API}/cobrettis`, data),
  update: (id, data) => axios.put(`${API}/cobrettis/${id}`, data),
  delete: (id) => axios.delete(`${API}/cobrettis/${id}`),
  cambiarEstado: (id, data) => axios.patch(`${API}/cobrettis/${id}/estado`, data),
  crearActuacion: (id) => axios.post(`${API}/cobrettis/${id}/crear-actuacion`),
  crearTarea: (id) => axios.post(`${API}/cobrettis/${id}/crear-tarea`),
  getRondas: (id) => axios.get(`${API}/cobrettis/${id}/rondas`),
  crearRonda: (id, data) => axios.post(`${API}/cobrettis/${id}/rondas`, data),
  getContactos: (id) => axios.get(`${API}/cobrettis/${id}/contactos`),
  crearContacto: (id, data) => axios.post(`${API}/cobrettis/${id}/contactos`, data),
  enviarEmail: (id, data) => axios.post(`${API}/cobrettis/${id}/contactos/email`, data),
  getPagos: (id) => axios.get(`${API}/cobrettis/${id}/pagos`),
  crearPago: (id, data) => axios.post(`${API}/cobrettis/${id}/pagos`, data),
  eliminarPago: (cobrettiId, pagoId) => axios.delete(`${API}/cobrettis/${cobrettiId}/pagos/${pagoId}`),
  getPlanPagos: (id) => axios.get(`${API}/cobrettis/${id}/plan-pagos`),
  crearPlanPagos: (id, data) => axios.post(`${API}/cobrettis/${id}/plan-pagos`, data),
  reclamarCuota: (cobrettiId, cuotaId) => axios.post(`${API}/cobrettis/${cobrettiId}/plan-pagos/${cuotaId}/reclamar`),
  getPlantillas: (params) => axios.get(`${API}/cobrettis/plantillas`, { params }),
  getPlantilla: (id) => axios.get(`${API}/cobrettis/plantillas/${id}`),
  crearPlantilla: (data) => axios.post(`${API}/cobrettis/plantillas`, data),
  actualizarPlantilla: (id, data) => axios.put(`${API}/cobrettis/plantillas/${id}`, data),
  eliminarPlantilla: (id) => axios.delete(`${API}/cobrettis/plantillas/${id}`),
  getAlertas: (params) => axios.get(`${API}/cobrettis/alertas`, { params }),
  marcarAlertaLeida: (id) => axios.patch(`${API}/cobrettis/alertas/${id}/leer`),
  descartarAlerta: (id) => axios.patch(`${API}/cobrettis/alertas/${id}/descartar`),
  verificarAlertas: () => axios.post(`${API}/cobrettis/verificar-alertas`),
  getTimeline: (id) => axios.get(`${API}/cobrettis/${id}/timeline`),
  getTimelineDetallado: (id, tipo) => axios.get(`${API}/cobrettis/${id}/timeline-detallado`, { params: { tipo } }),
  getByExpediente: (expedienteId) => axios.get(`${API}/cobrettis/expediente/${expedienteId}`),
  exportPdf: (id) => axios.get(`${API}/cobrettis/${id}/export-pdf`),
  getEstados: () => axios.get(`${API}/cobrettis/config/estados`),
  getTiposDeuda: () => axios.get(`${API}/cobrettis/config/tipos-deuda`),
  getVariablesPlantilla: () => axios.get(`${API}/cobrettis/config/variables-plantilla`),
  
  // Descarga segura de adjuntos (con autenticación)
  descargarAdjunto: (filename) => axios.get(`${API}/cobrettis/adjuntos/${filename}`, { responseType: 'blob' }),
};

// ============================================================================
// HONORARIOS - Seguimiento de cobro de honorarios
// ============================================================================
export const honorariosAPI = {
  getAll: (params) => axios.get(`${API}/honorarios`, { params }),
  get: (id) => axios.get(`${API}/honorarios/${id}`),
  create: (data) => axios.post(`${API}/honorarios`, data),
  update: (id, data) => axios.put(`${API}/honorarios/${id}`, data),
  delete: (id) => axios.delete(`${API}/honorarios/${id}`),
  getClienteExpediente: (expedienteId) => axios.get(`${API}/honorarios/expediente/${expedienteId}/cliente`),
  getEstadisticas: () => axios.get(`${API}/honorarios/estadisticas/resumen`),
  // Acciones
  createAccion: (id, data) => axios.post(`${API}/honorarios/${id}/acciones`, data),
  // Email
  getPlantillas: (id) => axios.get(`${API}/honorarios/${id}/plantillas-email`),
  getCuentasEmail: () => axios.get(`${API}/honorarios/cuentas-email/disponibles`),
  enviarEmail: (id, data) => axios.post(`${API}/honorarios/${id}/enviar-email`, data),
  // Pago
  registrarPago: (id, data) => axios.post(`${API}/honorarios/${id}/registrar-pago`, data),
  // [v139] descarga del listado en Excel, PDF, Word o Markdown
  exportar: (params) => axios.get(`${API}/honorarios/exportar`, { params, responseType: 'blob' }),
};

// ============================================================================
// AUTOMATIZACION - Emails Procuradores
// ============================================================================
export const automatizacionAPI = {
  // Config
  getConfig: () => axios.get(`${API}/automatizacion/config`),
  saveConfig: (data) => axios.post(`${API}/automatizacion/config`, data),
  testConnection: (data) => axios.post(`${API}/automatizacion/config/test-connection`, data),
  toggleActive: () => axios.post(`${API}/automatizacion/config/toggle-active`),

  // Emails Procuradores
  fetchEmails: (data) => axios.post(`${API}/automatizacion/emails-procuradores/fetch`, data),
  listEmails: (params) => axios.get(`${API}/automatizacion/emails-procuradores`, { params }),
  getEmail: (id) => axios.get(`${API}/automatizacion/emails-procuradores/${id}`),
  processIA: (id) => axios.post(`${API}/automatizacion/emails-procuradores/${id}/process-ia`),
  processAll: (emailIds) => axios.post(`${API}/automatizacion/emails-procuradores/process-all`, emailIds ? { email_ids: emailIds } : {}),
  editEmail: (id, data) => axios.put(`${API}/automatizacion/emails-procuradores/${id}`, data),
  confirmEmail: (id, data) => axios.post(`${API}/automatizacion/emails-procuradores/${id}/confirm`, data),
  discardEmail: (id) => axios.post(`${API}/automatizacion/emails-procuradores/${id}/discard`),
  restoreEmail: (id) => axios.post(`${API}/automatizacion/emails-procuradores/${id}/restore`),
  deleteEmail: (id) => axios.delete(`${API}/automatizacion/emails-procuradores/${id}`),
  reprocessEmail: (id) => axios.post(`${API}/automatizacion/emails-procuradores/${id}/reprocess`),
  getStats: () => axios.get(`${API}/automatizacion/emails-procuradores/stats/resumen`),

  // Buscar expedientes
  buscarExpedientes: (q) => axios.get(`${API}/automatizacion/expedientes/buscar`, { params: { q } }),

  // Progreso
  fetchProgress: (jobId) => axios.get(`${API}/automatizacion/fetch-progress/${jobId}`),

  // Notificaciones
  getPendingCount: () => axios.get(`${API}/automatizacion/notificaciones/pendientes`),

  // Preview PDF
  getPreviewPdfUrl: (emailId, adjuntoIdx) => `${API}/automatizacion/emails-procuradores/${emailId}/preview-pdf/${adjuntoIdx}`,

  // Reglas
  listReglas: () => axios.get(`${API}/automatizacion/reglas`),
  createRegla: (data) => axios.post(`${API}/automatizacion/reglas`, data),
  updateRegla: (id, data) => axios.put(`${API}/automatizacion/reglas/${id}`, data),
  deleteRegla: (id) => axios.delete(`${API}/automatizacion/reglas/${id}`),
  
  // Re-descarga adjuntos
  redownloadAttachments: () => axios.post(`${API}/automatizacion/redownload-attachments`),

  // Estadísticas avanzadas
  getEstadisticasKpis: () => axios.get(`${API}/automatizacion/estadisticas/kpis`),
  getEstadisticasProcurador: () => axios.get(`${API}/automatizacion/estadisticas/por-procurador`),
  getEstadisticasVolumen: (periodo) => axios.get(`${API}/automatizacion/estadisticas/volumen-temporal`, { params: { periodo } }),
  getEstadisticasTipoTramite: () => axios.get(`${API}/automatizacion/estadisticas/por-tipo-tramite`),
  getEstadisticasUsuario: (periodo) => axios.get(`${API}/automatizacion/estadisticas/por-usuario`, { params: { periodo } }),
  getEstadisticasHorasPunta: () => axios.get(`${API}/automatizacion/estadisticas/horas-punta`),
  getIAFeedbackStats: () => axios.get(`${API}/automatizacion/ia-feedback/stats`),
  getIAFeedbackHistorial: (params) => axios.get(`${API}/automatizacion/ia-feedback/historial`, { params }),
  getIAFeedbackEvolucion: () => axios.get(`${API}/automatizacion/ia-feedback/evolucion`),
  recalcularIAFeedback: () => axios.post(`${API}/automatizacion/ia-feedback/recalcular`),
  enviarEmailCliente: (emailId, data) => axios.post(`${API}/automatizacion/emails-procuradores/${emailId}/enviar-email-cliente`, data),
  borradorDecreto: (emailId, data) => axios.post(`${API}/automatizacion/emails-procuradores/${emailId}/borrador-decreto`, data),
};

// ============================================================================
// AUTOMATIZACION - Emails Clientes (info@lexduo.cat)
// ============================================================================
export const autoClientesAPI = {
  // Config
  getConfig: () => axios.get(`${API}/automatizacion/clientes/config`),
  saveConfig: (data) => axios.post(`${API}/automatizacion/clientes/config`, data),
  testConnection: (data) => axios.post(`${API}/automatizacion/clientes/config/test-connection`, data),

  // Emails
  fetchEmails: (data) => axios.post(`${API}/automatizacion/clientes/emails/fetch`, data),
  listEmails: (params) => axios.get(`${API}/automatizacion/clientes/emails`, { params }),
  getEmail: (id) => axios.get(`${API}/automatizacion/clientes/emails/${id}`),
  processIA: (id) => axios.post(`${API}/automatizacion/clientes/emails/${id}/process-ia`),
  processAll: (emailIds) => axios.post(`${API}/automatizacion/clientes/emails/process-all`, emailIds ? { email_ids: emailIds } : {}),
  processAllProgress: (jobId) => axios.get(`${API}/automatizacion/clientes/emails/process-all/progress/${jobId}`),
  processAllCancel: (jobId) => axios.post(`${API}/automatizacion/clientes/emails/process-all/cancel/${jobId}`),
  editEmail: (id, data) => axios.put(`${API}/automatizacion/clientes/emails/${id}`, data),
  tramitarEmail: (id, data) => axios.post(`${API}/automatizacion/clientes/emails/${id}/tramitar`, data),
  responderEmail: (id, data) => axios.post(`${API}/automatizacion/clientes/emails/${id}/responder`, data),
  discardEmail: (id) => axios.post(`${API}/automatizacion/clientes/emails/${id}/discard`),
  deleteEmail: (id) => axios.delete(`${API}/automatizacion/clientes/emails/${id}`),
  getStats: () => axios.get(`${API}/automatizacion/clientes/emails/stats/resumen`),
  getPreviewAdjuntoUrl: (emailId, adjuntoIdx) => `${API}/automatizacion/clientes/emails/${emailId}/preview-adjunto/${adjuntoIdx}`,

  // Search helpers
  buscarContactos: (q) => axios.get(`${API}/automatizacion/clientes/buscar-contactos`, { params: { q } }),
  buscarExpedientes: (q) => axios.get(`${API}/automatizacion/clientes/buscar-expedientes`, { params: { q } }),
  buscarConsultas: (q, contactoId) => axios.get(`${API}/automatizacion/clientes/buscar-consultas`, { params: { q, contacto_id: contactoId } }),

  // Plantillas
  getPlantillas: () => axios.get(`${API}/automatizacion/clientes/plantillas`),

  // Upload adjunto para respuesta
  uploadAdjunto: (formData) => axios.post(`${API}/automatizacion/clientes/upload-adjunto`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Estadisticas
  getEstadisticasKpis: () => axios.get(`${API}/automatizacion/clientes/estadisticas/kpis`),
  getEstadisticasRemitente: () => axios.get(`${API}/automatizacion/clientes/estadisticas/por-remitente`),
  getEstadisticasVolumen: (periodo) => axios.get(`${API}/automatizacion/clientes/estadisticas/volumen-temporal`, { params: { periodo } }),
  getEstadisticasClasificacion: () => axios.get(`${API}/automatizacion/clientes/estadisticas/por-clasificacion`),
  getEstadisticasTipoConsulta: () => axios.get(`${API}/automatizacion/clientes/estadisticas/por-tipo-consulta`),
  getEstadisticasUsuario: (periodo) => axios.get(`${API}/automatizacion/clientes/estadisticas/por-usuario`, { params: { periodo } }),
  getEstadisticasHorasPunta: () => axios.get(`${API}/automatizacion/clientes/estadisticas/horas-punta`),
};

// Pago de Tasas (Expediente)
export const pagosTasasAPI = {
  listByExpediente: (expedienteId) => axios.get(`${API}/expedientes/${expedienteId}/pagos-tasas`),
  createForExpediente: (expedienteId, data) => axios.post(`${API}/expedientes/${expedienteId}/pagos-tasas`, data),
  get: (id) => axios.get(`${API}/pagos-tasas/${id}`),
  update: (id, data) => axios.put(`${API}/pagos-tasas/${id}`, data),
  delete: (id) => axios.delete(`${API}/pagos-tasas/${id}`),
  listGlobal: (params) => axios.get(`${API}/pagos-tasas`, { params }),
  exportXlsx: (body) => axios.post(`${API}/pagos-tasas/export`, body, { responseType: 'blob' }),
};


// ==== Módulo Fiscalidad (Fase 1 + Fase 2) ====
// [v137] Inmuebles en arrendamiento y rendimiento del capital inmobiliario
export const inmueblesAPI = {
  listar: () => axios.get(`${API}/fiscalidad/inmuebles`).then((r) => r.data),
  crear: (datos) => axios.post(`${API}/fiscalidad/inmuebles`, datos).then((r) => r.data),
  actualizar: (id, datos) => axios.put(`${API}/fiscalidad/inmuebles/${id}`, datos).then((r) => r.data),
  borrar: (id) => axios.delete(`${API}/fiscalidad/inmuebles/${id}`).then((r) => r.data),
  crearContrato: (inmuebleId, datos) =>
    axios.post(`${API}/fiscalidad/inmuebles/${inmuebleId}/contratos`, datos).then((r) => r.data),
  actualizarContrato: (contratoId, datos) =>
    axios.put(`${API}/fiscalidad/inmuebles/contratos/${contratoId}`, datos).then((r) => r.data),
  borrarContrato: (contratoId) =>
    axios.delete(`${API}/fiscalidad/inmuebles/contratos/${contratoId}`).then((r) => r.data),
  asignarFactura: (facturaId, datos) =>
    axios.put(`${API}/fiscalidad/inmuebles/factura/${facturaId}`, datos).then((r) => r.data),
  asignarGasto: (gastoId, datos) =>
    axios.put(`${API}/fiscalidad/inmuebles/gasto/${gastoId}`, datos).then((r) => r.data),
  rendimiento: (ejercicio) =>
    axios.get(`${API}/fiscalidad/${ejercicio}/inmuebles`).then((r) => r.data),
  // [v138] documentos del inmueble y de sus contratos (escritura, contrato, IBI...)
  documentos: (inmuebleId) =>
    axios.get(`${API}/fiscalidad/inmuebles/${inmuebleId}/documentos`).then((r) => r.data),
  subirDocumentos: (inmuebleId, formData) =>
    axios.post(`${API}/fiscalidad/inmuebles/${inmuebleId}/documentos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data),
  descargarDocumento: (documentoId) =>
    axios.get(`${API}/fiscalidad/inmuebles/documentos/${documentoId}/descargar`,
      { responseType: 'blob' }),
  actualizarDocumento: (documentoId, datos) =>
    axios.put(`${API}/fiscalidad/inmuebles/documentos/${documentoId}`, datos).then((r) => r.data),
  borrarDocumento: (documentoId) =>
    axios.delete(`${API}/fiscalidad/inmuebles/documentos/${documentoId}`).then((r) => r.data),
};

export const fiscalidadAPI = {
  getConfig: (facturadorId) => axios.get(`${API}/fiscalidad/config`, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  updateConfig: (data, facturadorId) => axios.put(`${API}/fiscalidad/config`, data, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  getCodigosAEAT: () => axios.get(`${API}/fiscalidad/codigos-aeat`),
  sugerirCodigo: (texto) => axios.get(`${API}/fiscalidad/codigos-aeat/sugerir`, { params: { texto } }),
  getIVA: (ejercicio, trimestre, facturadorId) => axios.get(`${API}/fiscalidad/${ejercicio}/iva/${trimestre}`, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  getIRPF: (ejercicio, trimestre, facturadorId) => axios.get(`${API}/fiscalidad/${ejercicio}/irpf/${trimestre}`, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  // Modelo 111: criterio = 'pago' (fecha de pago y, si falta, la de factura) | 'factura'
  getRetenciones111: (ejercicio, trimestre, facturadorId, { criterio, complementaria } = {}) => axios.get(`${API}/fiscalidad/${ejercicio}/irpf111/${trimestre}`, { params: { ...(facturadorId ? { facturador_id: facturadorId } : {}), ...(criterio ? { criterio } : {}), ...(complementaria ? { complementaria: true } : {}) } }),
  // Clave de perceptor del 111/190 (se guarda en la ficha del contacto)
  getClavesPerceptor111: () => axios.get(`${API}/fiscalidad/claves-perceptor-111`),
  setClavePerceptor111: (contactoId, tipo) => axios.put(`${API}/fiscalidad/claves-perceptor-111/${contactoId}`, { tipo_perceptor_111: tipo || null }),
  // Avisos de plazo de los modelos del ejercicio (303, 130, 111, 390 y 190)
  getPlazos: (ejercicio, facturadorId) => axios.get(`${API}/fiscalidad/${ejercicio}/plazos`, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  // Modelo 190: resumen anual de retenciones, con cuadre contra los 111 presentados
  getModelo190: (ejercicio, facturadorId, { criterio } = {}) => axios.get(`${API}/fiscalidad/${ejercicio}/modelo190`, { params: { ...(facturadorId ? { facturador_id: facturadorId } : {}), ...(criterio ? { criterio } : {}) } }),
  getLiquidaciones: (ejercicio, trimestre, facturadorId, { criterio111, complementaria111 } = {}) => axios.get(`${API}/fiscalidad/${ejercicio}/liquidaciones/${trimestre}`, { params: { ...(facturadorId ? { facturador_id: facturadorId } : {}), ...(criterio111 ? { criterio_111: criterio111 } : {}), ...(complementaria111 ? { complementaria_111: true } : {}) } }),
  exportLiquidaciones: (ejercicio, trimestre, formato = 'excel', facturadorId, { criterio111 } = {}) =>
    axios.get(`${API}/fiscalidad/${ejercicio}/liquidaciones/${trimestre}/export`, { params: { formato, ...(facturadorId ? { facturador_id: facturadorId } : {}), ...(criterio111 ? { criterio_111: criterio111 } : {}) }, responseType: 'blob' }),
  // Liquidaciones ya presentadas ante la AEAT (alimentan la casilla 05 del 130)
  getPresentadas: (ejercicio, modelo, facturadorId) => axios.get(`${API}/fiscalidad/${ejercicio}/presentadas`, { params: { ...(modelo ? { modelo } : {}), ...(facturadorId ? { facturador_id: facturadorId } : {}) } }),
  guardarPresentada: (ejercicio, data, facturadorId) => axios.put(`${API}/fiscalidad/${ejercicio}/presentadas`, data, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  borrarPresentada: (ejercicio, modelo, trimestre, facturadorId) => axios.delete(`${API}/fiscalidad/${ejercicio}/presentadas/${modelo}/${trimestre}`, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  getResumen: (ejercicio, facturadorId) => axios.get(`${API}/fiscalidad/${ejercicio}/resumen`, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  getModelo390: (ejercicio, facturadorId) => axios.get(`${API}/fiscalidad/${ejercicio}/modelo390`, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  getModelo349: (ejercicio, trimestre, facturadorId) => axios.get(`${API}/fiscalidad/${ejercicio}/modelo349/${trimestre}`, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  getModelo349Anual: (ejercicio, facturadorId) => axios.get(`${API}/fiscalidad/${ejercicio}/modelo349`, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  getJustificantes: (ejercicio, { modelo, trimestre } = {}, facturadorId) => axios.get(`${API}/fiscalidad/${ejercicio}/justificantes`, { params: { ...(modelo ? { modelo } : {}), ...(trimestre ? { trimestre } : {}), ...(facturadorId ? { facturador_id: facturadorId } : {}) } }),
  subirJustificante: (ejercicio, { modelo, trimestre, tipo, notas }, archivo, facturadorId) => {
    const fd = new FormData();
    fd.append('modelo', modelo);
    if (trimestre) fd.append('trimestre', trimestre);
    fd.append('tipo', tipo || 'presentacion');
    if (notas) fd.append('notas', notas);
    if (facturadorId) fd.append('facturador_id', facturadorId);
    fd.append('archivo', archivo);
    return axios.post(`${API}/fiscalidad/${ejercicio}/justificantes`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  descargarJustificante: (ejercicio, id) => axios.get(`${API}/fiscalidad/${ejercicio}/justificantes/${id}/descargar`, { responseType: 'blob' }),
  borrarJustificante: (ejercicio, id) => axios.delete(`${API}/fiscalidad/${ejercicio}/justificantes/${id}`),
  getModelo100: (ejercicio, params = {}, facturadorId) => axios.get(`${API}/fiscalidad/${ejercicio}/modelo100`, { params: { ...params, ...(facturadorId ? { facturador_id: facturadorId } : {}) } }),
  librosRegistroAEAT: (ejercicio) => axios.get(`${API}/fiscalidad/${ejercicio}/libros-registro-aeat`, { responseType: 'blob' }),
  exportGestoria: (ejercicio, { trimestre = 0, formato = 'completo' } = {}) =>
    axios.get(`${API}/fiscalidad/${ejercicio}/export-gestoria`, { params: { trimestre, formato }, responseType: 'blob' }),
  // Informe completo: Excel + todas las facturas (PDF) en un ZIP. Puede tardar.
  exportGestoriaZip: (ejercicio, { trimestre = 0, formato = 'completo' } = {}) =>
    axios.get(`${API}/fiscalidad/${ejercicio}/export-gestoria-zip`, { params: { trimestre, formato }, responseType: 'blob', timeout: 900000 }),
};

export const bienesInversionAPI = {
  getTabla: () => axios.get(`${API}/bienes-inversion/tabla`),
  getAll: (facturadorId) => axios.get(`${API}/bienes-inversion`, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  getById: (id) => axios.get(`${API}/bienes-inversion/${id}`),
  create: (data) => axios.post(`${API}/bienes-inversion`, data),
  update: (id, data) => axios.put(`${API}/bienes-inversion/${id}`, data),
  delete: (id) => axios.delete(`${API}/bienes-inversion/${id}`),
  baja: (id, data) => axios.post(`${API}/bienes-inversion/${id}/baja`, data),
  getAmortizaciones: (ejercicio, facturadorId) => axios.get(`${API}/bienes-inversion/ejercicio/${ejercicio}/amortizaciones`, { params: facturadorId ? { facturador_id: facturadorId } : {} }),
  exportLibro: (ejercicio, formato = 'gestoria') => axios.get(`${API}/bienes-inversion/ejercicio/${ejercicio}/libro`, { params: { formato }, responseType: 'blob' }),
  getCandidatos: () => axios.get(`${API}/bienes-inversion/gastos-candidatos`),
  crearDesdeGasto: (gastoId, data) => axios.post(`${API}/bienes-inversion/desde-gasto/${gastoId}`, data),
  estadoCierre: (ejercicio) => axios.get(`${API}/bienes-inversion/ejercicio/${ejercicio}/estado-cierre`),
  cerrarEjercicio: (ejercicio) => axios.post(`${API}/bienes-inversion/ejercicio/${ejercicio}/cerrar`),
  regularizacionIva: (ejercicio, prorrataEjercicio) => axios.get(`${API}/bienes-inversion/ejercicio/${ejercicio}/regularizacion-iva`, { params: prorrataEjercicio != null && prorrataEjercicio !== '' ? { prorrata_ejercicio: prorrataEjercicio } : {} }),
};


// ==== Captura de Facturas por OCR/IA (Fiscalidad Fase 3) ====
export const capturaFacturasAPI = {
  upload: (formData) => axios.post(`${API}/captura-facturas/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  list: (params) => axios.get(`${API}/captura-facturas`, { params }),
  pendientesCount: () => axios.get(`${API}/captura-facturas/pendientes-count`),
  getById: (id) => axios.get(`${API}/captura-facturas/${id}`),
  update: (id, data) => axios.put(`${API}/captura-facturas/${id}`, data),
  confirmar: (id, data) => axios.post(`${API}/captura-facturas/${id}/confirmar`, data),
  descartar: (id) => axios.post(`${API}/captura-facturas/${id}/descartar`),
  reintentar: (id) => axios.post(`${API}/captura-facturas/${id}/reintentar`),
  eliminar: (id) => axios.delete(`${API}/captura-facturas/${id}`),
  previewUrl: (id) => `${API}/captura-facturas/${id}/preview`,
};
